import { $modelRelationships, $coreImplementation } from "@gadgetinc/core";
import { pipe, map } from "wonka";
import { GadgetConnection } from "./connection/GadgetConnection.js";
import { InternalModelManager } from "./connection/InternalModelManager.js";
import { AuthenticationMode } from "./connection/ClientOptions.js";
import { assert, GadgetClientError, disambiguateActionVariables, disambiguateBulkActionVariables, capitalizeIdentifier, namespaceDataPath, camelize, isEqual } from "./connection/support.js";
import { ErrorWrapper } from "./connection/ErrorWrapper.js";
import { GadgetRecord } from "./connection/GadgetRecord.js";
import { enqueueActionRunner } from "./connection/operationRunners.js";
import { BackgroundActionHandle } from "./connection/BackgroundActionHandle.js";
;
import { buildGlobalAction, buildComputedView, buildInlineComputedView } from "./builder.js";
import { WhatsappContactManager } from "./models/WhatsappContact.js";
import { CampaignStatsManager } from "./models/CampaignStats.js";
import { CustomCouponCodeManager } from "./models/CustomCouponCode.js";
import { ContactStatsManager } from "./models/ContactStats.js";
import { LibraryFlowManager } from "./models/LibraryFlow.js";
import { WhatsappTriggerManager } from "./models/WhatsappTrigger.js";
import { ShopifyPlanManager } from "./models/ShopifyPlan.js";
import { ReplyButtonManager } from "./models/ReplyButton.js";
import { FlowEdgeManager } from "./models/FlowEdge.js";
import { SendTemplateQueueManager } from "./models/SendTemplateQueue.js";
import { SessionManager } from "./models/Session.js";
import { CurrentSessionManager } from "./models/CurrentSession.js";
import { AbandonedCartAlertManager } from "./models/AbandonedCartAlert.js";
import { ConversationManager } from "./models/Conversation.js";
import { CampaignTemplateManager } from "./models/CampaignTemplate.js";
import { ContactListManager } from "./models/ContactList.js";
import { CampaignManager } from "./models/Campaign.js";
import { CampaignDayStatsManager } from "./models/CampaignDayStats.js";
import { FlowNodeManager } from "./models/FlowNode.js";
import { WhatsappTemplateManager } from "./models/WhatsappTemplate.js";
import { ShopifyAppInstallationManager } from "./models/ShopifyAppInstallation.js";
import { ShopifyAppPurchaseOneTimeManager } from "./models/ShopifyAppPurchaseOneTime.js";
import { ShopifyAppSubscriptionManager } from "./models/ShopifyAppSubscription.js";
import { ShopifyCartManager } from "./models/ShopifyCart.js";
import { ShopifyCartLineItemManager } from "./models/ShopifyCartLineItem.js";
import { ShopifyCheckoutManager } from "./models/ShopifyCheckout.js";
import { ShopifyCheckoutLineItemManager } from "./models/ShopifyCheckoutLineItem.js";
import { ShopifyCustomerManager } from "./models/ShopifyCustomer.js";
import { ShopifyFulfillmentManager } from "./models/ShopifyFulfillment.js";
import { ShopifyFulfillmentEventManager } from "./models/ShopifyFulfillmentEvent.js";
import { ShopifyFulfillmentLineItemManager } from "./models/ShopifyFulfillmentLineItem.js";
import { ShopifyGdprRequestManager } from "./models/ShopifyGdprRequest.js";
import { ShopifyOrderManager } from "./models/ShopifyOrder.js";
import { ShopifyOrderLineItemManager } from "./models/ShopifyOrderLineItem.js";
import { ShopifyProductManager } from "./models/ShopifyProduct.js";
import { ShopifyProductImageManager } from "./models/ShopifyProductImage.js";
import { ShopifyProductVariantManager } from "./models/ShopifyProductVariant.js";
import { ShopifyShippingAddressManager } from "./models/ShopifyShippingAddress.js";
import { ShopifyShopManager } from "./models/ShopifyShop.js";
import { ShopifySyncManager } from "./models/ShopifySync.js";
import { MessageManager } from "./models/Message.js";
import { ScenarioManager } from "./models/Scenario.js";
import { CsvImportManager } from "./models/CsvImport.js";
import { UserManager } from "./models/User.js";
import { BackInStockAlertManager } from "./models/BackInStockAlert.js";
import { TagManager } from "./models/Tag.js";
import { WaitingNodeManager } from "./models/WaitingNode.js";
import { MediaManager } from "./models/Media.js";
import { WaitingManager } from "./models/Waiting.js";
import { ContactManager } from "./models/Contact.js";
import { MessageSkeletonManager } from "./models/MessageSkeleton.js";
import { WhatsappWebhookManager } from "./models/WhatsappWebhook.js";
import { SendingManager } from "./models/Sending.js";
import { WhatsappRuleManager } from "./models/WhatsappRule.js";
import { CustomerManager } from "./models/Customer.js";
import { ContactTagManager } from "./models/ContactTag.js";
import { ConditionManager } from "./models/Condition.js";
import { AiSuggestionManager } from "./models/AiSuggestion.js";
import { GaiaWebhookManager } from "./models/GaiaWebhook.js";
import { StripeWebhookManager } from "./models/StripeWebhook.js";
import { OptinFormCompletionManager } from "./models/OptinFormCompletion.js";
import { RedirectionManager } from "./models/Redirection.js";
import { ReplyListManager } from "./models/ReplyList.js";
import { OfficialWhatsappTemplateManager } from "./models/OfficialWhatsappTemplate.js";
import { AiWebhookManager } from "./models/AiWebhook.js";
import { SamsungFormManager } from "./models/SamsungForm.js";
import { ProductLastUpdateManager } from "./models/ProductLastUpdate.js";
import { AiConfigurationManager } from "./models/AiConfiguration.js";
import { GlobalVariableManager } from "./models/GlobalVariable.js";
import { UrlClickEventManager } from "./models/UrlClickEvent.js";
import { WhatsappPartnerWebhookManager } from "./models/WhatsappPartnerWebhook.js";
import { CustomerStatsManager } from "./models/CustomerStats.js";
import { WebhookNodeManager } from "./models/WebhookNode.js";
import { CustomCouponCodeUsedManager } from "./models/CustomCouponCodeUsed.js";
import { UserCustomerManager } from "./models/UserCustomer.js";
import { SegmentMembershipManager } from "./models/SegmentMembership.js";
import { SegmentManager } from "./models/Segment.js";
import { ShopifyDraftOrderManager } from "./models/ShopifyDraftOrder.js";
import { ShopifyFileManager } from "./models/ShopifyFile.js";
import { ShopifyProductMediaManager } from "./models/ShopifyProductMedia.js";
import { ShopifyInventoryItemManager } from "./models/ShopifyInventoryItem.js";
import { ShopifyInventoryLevelManager } from "./models/ShopifyInventoryLevel.js";
import { ShopifyLocationManager } from "./models/ShopifyLocation.js";
import { FlowButtonManager } from "./models/FlowButton.js";
import { FlowButtonResponseManager } from "./models/FlowButtonResponse.js";
import { SendingStoppedManager } from "./models/SendingStopped.js";
import { SendingSummaryManager } from "./models/SendingSummary.js";
import { AlcmeonNamespace } from "./namespaces/alcmeon.js";
import { CleaningNamespace } from "./namespaces/cleaning.js";
import { HubspotNamespace } from "./namespaces/hubspot.js";
import { KlaviyoNamespace } from "./namespaces/klaviyo.js";
import { LoopsNamespace } from "./namespaces/loops.js";
import { OnboardingNamespace } from "./namespaces/onboarding.js";
import { RecoveryNamespace } from "./namespaces/recovery.js";
import { SettingsNamespace } from "./namespaces/settings.js";
import { SharedNamespace } from "./namespaces/shared.js";
import { DefaultWhatsappContactSelection as DefaultWhatsappContactSelection2 } from "./models/WhatsappContact.js";
import { DefaultCampaignStatsSelection as DefaultCampaignStatsSelection2 } from "./models/CampaignStats.js";
import { DefaultCustomCouponCodeSelection as DefaultCustomCouponCodeSelection2 } from "./models/CustomCouponCode.js";
import { DefaultContactStatsSelection as DefaultContactStatsSelection2 } from "./models/ContactStats.js";
import { DefaultLibraryFlowSelection as DefaultLibraryFlowSelection2 } from "./models/LibraryFlow.js";
import { DefaultWhatsappTriggerSelection as DefaultWhatsappTriggerSelection2 } from "./models/WhatsappTrigger.js";
import { DefaultShopifyPlanSelection as DefaultShopifyPlanSelection2 } from "./models/ShopifyPlan.js";
import { DefaultReplyButtonSelection as DefaultReplyButtonSelection2 } from "./models/ReplyButton.js";
import { DefaultFlowEdgeSelection as DefaultFlowEdgeSelection2 } from "./models/FlowEdge.js";
import { DefaultSendTemplateQueueSelection as DefaultSendTemplateQueueSelection2 } from "./models/SendTemplateQueue.js";
import { DefaultSessionSelection as DefaultSessionSelection2 } from "./models/Session.js";
import { DefaultAbandonedCartAlertSelection as DefaultAbandonedCartAlertSelection2 } from "./models/AbandonedCartAlert.js";
import { DefaultConversationSelection as DefaultConversationSelection2 } from "./models/Conversation.js";
import { DefaultCampaignTemplateSelection as DefaultCampaignTemplateSelection2 } from "./models/CampaignTemplate.js";
import { DefaultContactListSelection as DefaultContactListSelection2 } from "./models/ContactList.js";
import { DefaultCampaignSelection as DefaultCampaignSelection2 } from "./models/Campaign.js";
import { DefaultCampaignDayStatsSelection as DefaultCampaignDayStatsSelection2 } from "./models/CampaignDayStats.js";
import { DefaultFlowNodeSelection as DefaultFlowNodeSelection2 } from "./models/FlowNode.js";
import { DefaultWhatsappTemplateSelection as DefaultWhatsappTemplateSelection2 } from "./models/WhatsappTemplate.js";
import { DefaultShopifyAppInstallationSelection as DefaultShopifyAppInstallationSelection2 } from "./models/ShopifyAppInstallation.js";
import { DefaultShopifyAppPurchaseOneTimeSelection as DefaultShopifyAppPurchaseOneTimeSelection2 } from "./models/ShopifyAppPurchaseOneTime.js";
import { DefaultShopifyAppSubscriptionSelection as DefaultShopifyAppSubscriptionSelection2 } from "./models/ShopifyAppSubscription.js";
import { DefaultShopifyCartSelection as DefaultShopifyCartSelection2 } from "./models/ShopifyCart.js";
import { DefaultShopifyCartLineItemSelection as DefaultShopifyCartLineItemSelection2 } from "./models/ShopifyCartLineItem.js";
import { DefaultShopifyCheckoutSelection as DefaultShopifyCheckoutSelection2 } from "./models/ShopifyCheckout.js";
import { DefaultShopifyCheckoutLineItemSelection as DefaultShopifyCheckoutLineItemSelection2 } from "./models/ShopifyCheckoutLineItem.js";
import { DefaultShopifyCustomerSelection as DefaultShopifyCustomerSelection2 } from "./models/ShopifyCustomer.js";
import { DefaultShopifyFulfillmentSelection as DefaultShopifyFulfillmentSelection2 } from "./models/ShopifyFulfillment.js";
import { DefaultShopifyFulfillmentEventSelection as DefaultShopifyFulfillmentEventSelection2 } from "./models/ShopifyFulfillmentEvent.js";
import { DefaultShopifyFulfillmentLineItemSelection as DefaultShopifyFulfillmentLineItemSelection2 } from "./models/ShopifyFulfillmentLineItem.js";
import { DefaultShopifyGdprRequestSelection as DefaultShopifyGdprRequestSelection2 } from "./models/ShopifyGdprRequest.js";
import { DefaultShopifyOrderSelection as DefaultShopifyOrderSelection2 } from "./models/ShopifyOrder.js";
import { DefaultShopifyOrderLineItemSelection as DefaultShopifyOrderLineItemSelection2 } from "./models/ShopifyOrderLineItem.js";
import { DefaultShopifyProductSelection as DefaultShopifyProductSelection2 } from "./models/ShopifyProduct.js";
import { DefaultShopifyProductImageSelection as DefaultShopifyProductImageSelection2 } from "./models/ShopifyProductImage.js";
import { DefaultShopifyProductVariantSelection as DefaultShopifyProductVariantSelection2 } from "./models/ShopifyProductVariant.js";
import { DefaultShopifyShippingAddressSelection as DefaultShopifyShippingAddressSelection2 } from "./models/ShopifyShippingAddress.js";
import { DefaultShopifyShopSelection as DefaultShopifyShopSelection2 } from "./models/ShopifyShop.js";
import { DefaultShopifySyncSelection as DefaultShopifySyncSelection2 } from "./models/ShopifySync.js";
import { DefaultMessageSelection as DefaultMessageSelection2 } from "./models/Message.js";
import { DefaultScenarioSelection as DefaultScenarioSelection2 } from "./models/Scenario.js";
import { DefaultCsvImportSelection as DefaultCsvImportSelection2 } from "./models/CsvImport.js";
import { DefaultUserSelection as DefaultUserSelection2 } from "./models/User.js";
import { DefaultBackInStockAlertSelection as DefaultBackInStockAlertSelection2 } from "./models/BackInStockAlert.js";
import { DefaultTagSelection as DefaultTagSelection2 } from "./models/Tag.js";
import { DefaultWaitingNodeSelection as DefaultWaitingNodeSelection2 } from "./models/WaitingNode.js";
import { DefaultMediaSelection as DefaultMediaSelection2 } from "./models/Media.js";
import { DefaultWaitingSelection as DefaultWaitingSelection2 } from "./models/Waiting.js";
import { DefaultContactSelection as DefaultContactSelection2 } from "./models/Contact.js";
import { DefaultMessageSkeletonSelection as DefaultMessageSkeletonSelection2 } from "./models/MessageSkeleton.js";
import { DefaultWhatsappWebhookSelection as DefaultWhatsappWebhookSelection2 } from "./models/WhatsappWebhook.js";
import { DefaultSendingSelection as DefaultSendingSelection2 } from "./models/Sending.js";
import { DefaultWhatsappRuleSelection as DefaultWhatsappRuleSelection2 } from "./models/WhatsappRule.js";
import { DefaultCustomerSelection as DefaultCustomerSelection2 } from "./models/Customer.js";
import { DefaultContactTagSelection as DefaultContactTagSelection2 } from "./models/ContactTag.js";
import { DefaultConditionSelection as DefaultConditionSelection2 } from "./models/Condition.js";
import { DefaultAiSuggestionSelection as DefaultAiSuggestionSelection2 } from "./models/AiSuggestion.js";
import { DefaultGaiaWebhookSelection as DefaultGaiaWebhookSelection2 } from "./models/GaiaWebhook.js";
import { DefaultStripeWebhookSelection as DefaultStripeWebhookSelection2 } from "./models/StripeWebhook.js";
import { DefaultOptinFormCompletionSelection as DefaultOptinFormCompletionSelection2 } from "./models/OptinFormCompletion.js";
import { DefaultRedirectionSelection as DefaultRedirectionSelection2 } from "./models/Redirection.js";
import { DefaultReplyListSelection as DefaultReplyListSelection2 } from "./models/ReplyList.js";
import { DefaultOfficialWhatsappTemplateSelection as DefaultOfficialWhatsappTemplateSelection2 } from "./models/OfficialWhatsappTemplate.js";
import { DefaultAiWebhookSelection as DefaultAiWebhookSelection2 } from "./models/AiWebhook.js";
import { DefaultSamsungFormSelection as DefaultSamsungFormSelection2 } from "./models/SamsungForm.js";
import { DefaultProductLastUpdateSelection as DefaultProductLastUpdateSelection2 } from "./models/ProductLastUpdate.js";
import { DefaultAiConfigurationSelection as DefaultAiConfigurationSelection2 } from "./models/AiConfiguration.js";
import { DefaultGlobalVariableSelection as DefaultGlobalVariableSelection2 } from "./models/GlobalVariable.js";
import { DefaultUrlClickEventSelection as DefaultUrlClickEventSelection2 } from "./models/UrlClickEvent.js";
import { DefaultWhatsappPartnerWebhookSelection as DefaultWhatsappPartnerWebhookSelection2 } from "./models/WhatsappPartnerWebhook.js";
import { DefaultCustomerStatsSelection as DefaultCustomerStatsSelection2 } from "./models/CustomerStats.js";
import { DefaultWebhookNodeSelection as DefaultWebhookNodeSelection2 } from "./models/WebhookNode.js";
import { DefaultCustomCouponCodeUsedSelection as DefaultCustomCouponCodeUsedSelection2 } from "./models/CustomCouponCodeUsed.js";
import { DefaultUserCustomerSelection as DefaultUserCustomerSelection2 } from "./models/UserCustomer.js";
import { DefaultSegmentMembershipSelection as DefaultSegmentMembershipSelection2 } from "./models/SegmentMembership.js";
import { DefaultSegmentSelection as DefaultSegmentSelection2 } from "./models/Segment.js";
import { DefaultShopifyDraftOrderSelection as DefaultShopifyDraftOrderSelection2 } from "./models/ShopifyDraftOrder.js";
import { DefaultShopifyFileSelection as DefaultShopifyFileSelection2 } from "./models/ShopifyFile.js";
import { DefaultShopifyProductMediaSelection as DefaultShopifyProductMediaSelection2 } from "./models/ShopifyProductMedia.js";
import { DefaultShopifyInventoryItemSelection as DefaultShopifyInventoryItemSelection2 } from "./models/ShopifyInventoryItem.js";
import { DefaultShopifyInventoryLevelSelection as DefaultShopifyInventoryLevelSelection2 } from "./models/ShopifyInventoryLevel.js";
import { DefaultShopifyLocationSelection as DefaultShopifyLocationSelection2 } from "./models/ShopifyLocation.js";
import { DefaultFlowButtonSelection as DefaultFlowButtonSelection2 } from "./models/FlowButton.js";
import { DefaultFlowButtonResponseSelection as DefaultFlowButtonResponseSelection2 } from "./models/FlowButtonResponse.js";
import { DefaultSendingStoppedSelection as DefaultSendingStoppedSelection2 } from "./models/SendingStopped.js";
import { DefaultSendingSummarySelection as DefaultSendingSummarySelection2 } from "./models/SendingSummary.js";
const productionEnv = "production";
const fallbackEnv = "development";
const availableAuthenticationModes = [
  "apiKey",
  "browserSession",
  "anonymous",
  "internalAuthToken",
  "internal",
  "custom"
];
const maybeGetAuthenticationModeOptionsFromClientOptions = (options) => {
  const topLevelAuthModes = {};
  for (const key of availableAuthenticationModes) {
    if (key in options) {
      topLevelAuthModes[key] = options[key];
    }
  }
  if ("authenticationMode" in options && Object.keys(topLevelAuthModes).length > 0) {
    throw new GadgetClientError(
      "Declaring authentication modes at the top level and under the `authenticationMode` key at the same time is not allowed."
    );
  }
  if ("authenticationMode" in options) {
    return options.authenticationMode;
  }
  if (Object.keys(topLevelAuthModes).length === 0) {
    return void 0;
  }
  return topLevelAuthModes;
};
const getImplicitEnv = () => {
  try {
    return process.env.GADGET_ENV;
  } catch (error) {
    return void 0;
  }
};
class GetsimioClient {
  constructor(options) {
    this.$args = Symbol.for("gadget/fieldArgs");
    /** Executes the addCampaignDayStats global action. */
    this.addCampaignDayStats = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "addCampaignDayStats",
      operationName: "addCampaignDayStats",
      operationReturnType: "AddCampaignDayStats",
      namespace: null,
      variables: {}
    });
    /** Executes the addContactsToSegment global action. */
    this.addContactsToSegment = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "addContactsToSegment",
      operationName: "addContactsToSegment",
      operationReturnType: "AddContactsToSegment",
      namespace: null,
      variables: {
        segmentId: { required: false, type: "String" },
        contactIds: { required: false, type: "[String!]" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the addProductsToGaiaCustomDatasource global action. */
    this.addProductsToGaiaCustomDatasource = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "addProductsToGaiaCustomDatasource",
      operationName: "addProductsToGaiaCustomDatasource",
      operationReturnType: "AddProductsToGaiaCustomDatasource",
      namespace: null,
      variables: {
        datasourceId: { required: false, type: "String" },
        shopId: { required: false, type: "String" }
      }
    });
    /** Executes the addSendingSummary global action. */
    this.addSendingSummary = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "addSendingSummary",
      operationName: "addSendingSummary",
      operationReturnType: "AddSendingSummary",
      namespace: null,
      variables: {
        audiencePerDay: { required: false, type: "[[String!]!]" },
        sendingBatchId: { required: false, type: "Float" },
        campaignId: { required: false, type: "String" }
      }
    });
    /** Executes the addTagToContact global action. */
    this.addTagToContact = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "addTagToContact",
      operationName: "addTagToContact",
      operationReturnType: "AddTagToContact",
      namespace: null,
      variables: {
        nodeId: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        contactNumber: { required: false, type: "String" }
      }
    });
    /** Executes the alertIfSendingFailed global action. */
    this.alertIfSendingFailed = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "alertIfSendingFailed",
      operationName: "alertIfSendingFailed",
      operationReturnType: "AlertIfSendingFailed",
      namespace: null,
      variables: {}
    });
    /** Executes the alertParrainParrainage global action. */
    this.alertParrainParrainage = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "alertParrainParrainage",
      operationName: "alertParrainParrainage",
      operationReturnType: "AlertParrainParrainage",
      namespace: null,
      variables: {
        filleulPhoneNumber: { required: false, type: "String" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the answerWithGaia global action. */
    this.answerWithGaia = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "answerWithGaia",
      operationName: "answerWithGaia",
      operationReturnType: "AnswerWithGaia",
      namespace: null,
      variables: {
        lastGaiaAiConfigurationId: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        contactNumber: { required: false, type: "String" },
        body: { required: false, type: "String" }
      }
    });
    /** Executes the answerWithOwnAi global action. */
    this.answerWithOwnAi = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "answerWithOwnAi",
      operationName: "answerWithOwnAi",
      operationReturnType: "AnswerWithOwnAi",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        contactNumber: { required: false, type: "String" },
        conversationId: { required: false, type: "String" },
        body: { required: false, type: "String" },
        voice: { required: false, type: "String" },
        image: { required: false, type: "String" },
        video: { required: false, type: "String" },
        couponCode: { required: false, type: "String" }
      }
    });
    /** Executes the archiveSegment global action. */
    this.archiveSegment = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "archiveSegment",
      operationName: "archiveSegment",
      operationReturnType: "ArchiveSegment",
      namespace: null,
      variables: {
        segmentId: { required: false, type: "String" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the attributeTurnoverFromSplio global action. */
    this.attributeTurnoverFromSplio = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "attributeTurnoverFromSplio",
      operationName: "attributeTurnoverFromSplio",
      operationReturnType: "AttributeTurnoverFromSplio",
      namespace: null,
      variables: {
        token: { required: false, type: "String" },
        email: { required: false, type: "String" },
        messageId: { required: false, type: "String" },
        messageCreatedAt: { required: false, type: "String" }
      }
    });
    /** Executes the attributeTurnoverFromSplioForOnePage global action. */
    this.attributeTurnoverFromSplioForOnePage = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "attributeTurnoverFromSplioForOnePage",
      operationName: "attributeTurnoverFromSplioForOnePage",
      operationReturnType: "AttributeTurnoverFromSplioForOnePage",
      namespace: null,
      variables: {
        token: { required: false, type: "String" },
        endCursor: { required: false, type: "String" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the babaOptins global action. */
    this.babaOptins = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "babaOptins",
      operationName: "babaOptins",
      operationReturnType: "BabaOptins",
      namespace: null,
      variables: {}
    });
    /** Executes the bulkUpdateMessageContacts global action. */
    this.bulkUpdateMessageContacts = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "bulkUpdateMessageContacts",
      operationName: "bulkUpdateMessageContacts",
      operationReturnType: "BulkUpdateMessageContacts",
      namespace: null,
      variables: {
        updates: {
          required: false,
          type: "[BulkUpdateMessageContactsUpdatesElementTypeInput!]"
        }
      }
    });
    /** Executes the cancelRegisteredBatch global action. */
    this.cancelRegisteredBatch = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "cancelRegisteredBatch",
      operationName: "cancelRegisteredBatch",
      operationReturnType: "CancelRegisteredBatch",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        sendingBatchId: { required: false, type: "Float" }
      }
    });
    /** Executes the cancelStoppedCampaign global action. */
    this.cancelStoppedCampaign = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "cancelStoppedCampaign",
      operationName: "cancelStoppedCampaign",
      operationReturnType: "CancelStoppedCampaign",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        sendingBatchId: { required: false, type: "Float" }
      }
    });
    /** Executes the cancelSubscription global action. */
    this.cancelSubscription = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "cancelSubscription",
      operationName: "cancelSubscription",
      operationReturnType: "CancelSubscription",
      namespace: null,
      variables: {
        appSubscriptionId: { required: false, type: "String" },
        shopId: { required: false, type: "String" }
      }
    });
    /** Executes the checkCustomer875Messages global action. */
    this.checkCustomer875Messages = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "checkCustomer875Messages",
      operationName: "checkCustomer875Messages",
      operationReturnType: "CheckCustomer875Messages",
      namespace: null,
      variables: {}
    });
    /** Executes the cleanCorruptedFlowEdges global action. */
    this.cleanCorruptedFlowEdges = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "cleanCorruptedFlowEdges",
      operationName: "cleanCorruptedFlowEdges",
      operationReturnType: "CleanCorruptedFlowEdges",
      namespace: null,
      variables: {
        scenarioId: { required: false, type: "String" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the cleanExternalTurnover global action. */
    this.cleanExternalTurnover = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "cleanExternalTurnover",
      operationName: "cleanExternalTurnover",
      operationReturnType: "CleanExternalTurnover",
      namespace: null,
      variables: {}
    });
    /** Executes the cleanPhoneDatabase global action. */
    this.cleanPhoneDatabase = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "cleanPhoneDatabase",
      operationName: "cleanPhoneDatabase",
      operationReturnType: "CleanPhoneDatabase",
      namespace: null,
      variables: {
        traceId: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        execute: { required: false, type: "Boolean" },
        limit: { required: false, type: "Float" },
        skipConversations: { required: false, type: "Boolean" }
      }
    });
    /** Executes the computePastCampaignAttribution global action. */
    this.computePastCampaignAttribution = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "computePastCampaignAttribution",
      operationName: "computePastCampaignAttribution",
      operationReturnType: "ComputePastCampaignAttribution",
      namespace: null,
      variables: {}
    });
    /** Executes the copyCampaignNodesAndEdges global action. */
    this.copyCampaignNodesAndEdges = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "copyCampaignNodesAndEdges",
      operationName: "copyCampaignNodesAndEdges",
      operationReturnType: "CopyCampaignNodesAndEdges",
      namespace: null,
      variables: {
        oldCampaignId: { required: false, type: "String" },
        oldScenarioId: { required: false, type: "String" },
        campaignName: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        isShared: { required: false, type: "Boolean" },
        libraryFlowId: { required: false, type: "String" }
      }
    });
    /** Executes the correctMessageStatus global action. */
    this.correctMessageStatus = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "correctMessageStatus",
      operationName: "correctMessageStatus",
      operationReturnType: "CorrectMessageStatus",
      namespace: null,
      variables: {}
    });
    /** Executes the correctOptinVW global action. */
    this.correctOptinVW = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "correctOptinVW",
      operationName: "correctOptinVW",
      operationReturnType: "CorrectOptinVW",
      namespace: null,
      variables: {}
    });
    /** Executes the correctReadStatus global action. */
    this.correctReadStatus = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "correctReadStatus",
      operationName: "correctReadStatus",
      operationReturnType: "CorrectReadStatus",
      namespace: null,
      variables: {}
    });
    /** Executes the correctWrongCustomerIdForContacts global action. */
    this.correctWrongCustomerIdForContacts = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "correctWrongCustomerIdForContacts",
      operationName: "correctWrongCustomerIdForContacts",
      operationReturnType: "CorrectWrongCustomerIdForContacts",
      namespace: null,
      variables: {}
    });
    /** Executes the createAbandonedCartDiscount global action. */
    this.createAbandonedCartDiscount = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "createAbandonedCartDiscount",
      operationName: "createAbandonedCartDiscount",
      operationReturnType: "CreateAbandonedCartDiscount",
      namespace: null,
      variables: {
        shopId: { required: false, type: "String" },
        shopifyCustomerId: { required: false, type: "String" },
        percentageDiscount: { required: false, type: "Float" }
      }
    });
    /** Executes the createBulkCustomCouponCodes global action. */
    this.createBulkCustomCouponCodes = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "createBulkCustomCouponCodes",
      operationName: "createBulkCustomCouponCodes",
      operationReturnType: "CreateBulkCustomCouponCodes",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        codes: { required: false, type: "[String!]" },
        category: { required: false, type: "String" }
      }
    });
    /** Executes the createContactFromImport global action. */
    this.createContactFromImport = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "createContactFromImport",
      operationName: "createContactFromImport",
      operationReturnType: "CreateContactFromImport",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        tagId: { required: false, type: "String" },
        row: { required: false, type: "String" }
      }
    });
    /** Executes the createContactsFromCheckoutAndSignUpForms global action. */
    this.createContactsFromCheckoutAndSignUpForms = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "createContactsFromCheckoutAndSignUpForms",
      operationName: "createContactsFromCheckoutAndSignUpForms",
      operationReturnType: "CreateContactsFromCheckoutAndSignUpForms",
      namespace: null,
      variables: {}
    });
    /** Executes the createContactsFromKlaviyo global action. */
    this.createContactsFromKlaviyo = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "createContactsFromKlaviyo",
      operationName: "createContactsFromKlaviyo",
      operationReturnType: "CreateContactsFromKlaviyo",
      namespace: null,
      variables: {}
    });
    /** Executes the createCustomerStatsRecords global action. */
    this.createCustomerStatsRecords = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "createCustomerStatsRecords",
      operationName: "createCustomerStatsRecords",
      operationReturnType: "CreateCustomerStatsRecords",
      namespace: null,
      variables: {}
    });
    /** Executes the createMockedShopifyOrder global action. */
    this.createMockedShopifyOrder = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "createMockedShopifyOrder",
      operationName: "createMockedShopifyOrder",
      operationReturnType: "CreateMockedShopifyOrder",
      namespace: null,
      variables: {
        shopId: { required: false, type: "String" },
        totalPrice: { required: false, type: "String" },
        currency: { required: false, type: "String" },
        campaignOriginId: { required: false, type: "String" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the createNewContactBatch global action. */
    this.createNewContactBatch = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "createNewContactBatch",
      operationName: "createNewContactBatch",
      operationReturnType: "CreateNewContactBatch",
      namespace: null,
      variables: {
        tagId: { required: false, type: "String" },
        shouldSkipOnSuccess: { required: false, type: "Boolean" },
        contacts: { required: false, type: "[JSONObject!]" }
      }
    });
    /** Executes the createNewContactBatchWithMultipleTags global action. */
    this.createNewContactBatchWithMultipleTags = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "createNewContactBatchWithMultipleTags",
      operationName: "createNewContactBatchWithMultipleTags",
      operationReturnType: "CreateNewContactBatchWithMultipleTags",
      namespace: null,
      variables: {
        tagIds: { required: false, type: "[String!]" },
        csvImportId: { required: false, type: "String" },
        shouldSkipOnSuccess: { required: false, type: "Boolean" },
        contacts: { required: false, type: "[JSONObject!]" }
      }
    });
    /** Executes the createSegmentFromTags global action. */
    this.createSegmentFromTags = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "createSegmentFromTags",
      operationName: "createSegmentFromTags",
      operationReturnType: "CreateSegmentFromTags",
      namespace: null,
      variables: {
        tagName: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        orTagsIds: { required: false, type: "[String!]" },
        andTagsIds: { required: false, type: "[String!]" }
      }
    });
    /** Executes the createStripeCheckoutSession global action. */
    this.createStripeCheckoutSession = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "createStripeCheckoutSession",
      operationName: "createStripeCheckoutSession",
      operationReturnType: "CreateStripeCheckoutSession",
      namespace: null,
      variables: { customerId: { required: false, type: "String" } }
    });
    /** Executes the createUpsellDraftOrder global action. */
    this.createUpsellDraftOrder = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "createUpsellDraftOrder",
      operationName: "createUpsellDraftOrder",
      operationReturnType: "CreateUpsellDraftOrder",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        shopifyUpsellLineItems: { required: false, type: "String" },
        orderName: { required: false, type: "String" },
        phoneNumber: { required: false, type: "String" }
      }
    });
    /** Executes the customerOrdersOptin global action. */
    this.customerOrdersOptin = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "customerOrdersOptin",
      operationName: "customerOrdersOptin",
      operationReturnType: "CustomerOrdersOptin",
      namespace: null,
      variables: {}
    });
    /** Executes the customersWithPhone global action. */
    this.customersWithPhone = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "customersWithPhone",
      operationName: "customersWithPhone",
      operationReturnType: "CustomersWithPhone",
      namespace: null,
      variables: {}
    });
    /** Executes the dailySendingRecap global action. */
    this.dailySendingRecap = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "dailySendingRecap",
      operationName: "dailySendingRecap",
      operationReturnType: "DailySendingRecap",
      namespace: null,
      variables: {}
    });
    /** Executes the dedupSamsungContact global action. */
    this.dedupSamsungContact = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "dedupSamsungContact",
      operationName: "dedupSamsungContact",
      operationReturnType: "DedupSamsungContact",
      namespace: null,
      variables: {
        oldId: { required: false, type: "String" },
        newId: { required: false, type: "String" }
      }
    });
    /** Executes the delete360dialogTemplate global action. */
    this.delete360dialogTemplate = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "delete360dialogTemplate",
      operationName: "delete360dialogTemplate",
      operationReturnType: "Delete360dialogTemplate",
      namespace: null,
      variables: {
        templateName: { required: false, type: "String" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the deleteAbandonedCartAlerts global action. */
    this.deleteAbandonedCartAlerts = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "deleteAbandonedCartAlerts",
      operationName: "deleteAbandonedCartAlerts",
      operationReturnType: "DeleteAbandonedCartAlerts",
      namespace: null,
      variables: {}
    });
    /** Executes the deleteContactTags global action. */
    this.deleteContactTags = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "deleteContactTags",
      operationName: "deleteContactTags",
      operationReturnType: "DeleteContactTags",
      namespace: null,
      variables: {
        tagId: { required: false, type: "String" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the deleteDummyContacts global action. */
    this.deleteDummyContacts = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "deleteDummyContacts",
      operationName: "deleteDummyContacts",
      operationReturnType: "DeleteDummyContacts",
      namespace: null,
      variables: {}
    });
    /** Executes the deleteEdgesLinkedToANode global action. */
    this.deleteEdgesLinkedToANode = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "deleteEdgesLinkedToANode",
      operationName: "deleteEdgesLinkedToANode",
      operationReturnType: "DeleteEdgesLinkedToANode",
      namespace: null,
      variables: { flowNodeId: { required: false, type: "String" } }
    });
    /** Executes the deleteUpsellDraftOrder global action. */
    this.deleteUpsellDraftOrder = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "deleteUpsellDraftOrder",
      operationName: "deleteUpsellDraftOrder",
      operationReturnType: "DeleteUpsellDraftOrder",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        draftOrderId: { required: false, type: "String" }
      }
    });
    /** Executes the downloadGorgiasMediaFromUrl global action. */
    this.downloadGorgiasMediaFromUrl = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "downloadGorgiasMediaFromUrl",
      operationName: "downloadGorgiasMediaFromUrl",
      operationReturnType: "DownloadGorgiasMediaFromUrl",
      namespace: null,
      variables: {
        url: { required: false, type: "String" },
        apiKeyGorgias: { required: false, type: "String" },
        hostGorgias: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        mediaType: { required: false, type: "String" }
      }
    });
    /** Executes the duplicateCampaignNodesAndEdges global action. */
    this.duplicateCampaignNodesAndEdges = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "duplicateCampaignNodesAndEdges",
      operationName: "duplicateCampaignNodesAndEdges",
      operationReturnType: "DuplicateCampaignNodesAndEdges",
      namespace: null,
      variables: {
        oldCampaignId: { required: false, type: "String" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the duplicateChatbotNodesAndEdges global action. */
    this.duplicateChatbotNodesAndEdges = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "duplicateChatbotNodesAndEdges",
      operationName: "duplicateChatbotNodesAndEdges",
      operationReturnType: "DuplicateChatbotNodesAndEdges",
      namespace: null,
      variables: {
        oldScenarioId: { required: false, type: "String" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the enqueueContactsExportForAllCustomers global action. */
    this.enqueueContactsExportForAllCustomers = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "enqueueContactsExportForAllCustomers",
      operationName: "enqueueContactsExportForAllCustomers",
      operationReturnType: "EnqueueContactsExportForAllCustomers",
      namespace: null,
      variables: {}
    });
    /** Executes the enqueueTrackingExportForAllCustomers global action. */
    this.enqueueTrackingExportForAllCustomers = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "enqueueTrackingExportForAllCustomers",
      operationName: "enqueueTrackingExportForAllCustomers",
      operationReturnType: "EnqueueTrackingExportForAllCustomers",
      namespace: null,
      variables: {}
    });
    /** Executes the eraseContactsOptinsMoreThanOneYearAgoShopify global action. */
    this.eraseContactsOptinsMoreThanOneYearAgoShopify = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "eraseContactsOptinsMoreThanOneYearAgoShopify",
      operationName: "eraseContactsOptinsMoreThanOneYearAgoShopify",
      operationReturnType: "EraseContactsOptinsMoreThanOneYearAgoShopify",
      namespace: null,
      variables: { customerId: { required: false, type: "String" } }
    });
    /** Executes the eraseGdprContactsForOccitane global action. */
    this.eraseGdprContactsForOccitane = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "eraseGdprContactsForOccitane",
      operationName: "eraseGdprContactsForOccitane",
      operationReturnType: "EraseGdprContactsForOccitane",
      namespace: null,
      variables: {}
    });
    /** Executes the executeConditionNode global action. */
    this.executeConditionNode = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "executeConditionNode",
      operationName: "executeConditionNode",
      operationReturnType: "ExecuteConditionNode",
      namespace: null,
      variables: {
        nodeId: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        contactNumber: { required: false, type: "String" },
        abandonedCheckoutUrl: { required: false, type: "String" }
      }
    });
    /** Executes the executeNode global action. */
    this.executeNode = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "executeNode",
      operationName: "executeNode",
      operationReturnType: "ExecuteNode",
      namespace: null,
      variables: {
        nodeId: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        contactNumber: { required: false, type: "String" },
        isAnswer: { required: false, type: "Boolean" },
        isStatusChange: { required: false, type: "Boolean" },
        isWaitingCompleted: { required: false, type: "Boolean" },
        abandonedCheckoutUrl: { required: false, type: "String" },
        skipAction: { required: false, type: "Boolean" }
      }
    });
    /** Executes the executeWaitings global action. */
    this.executeWaitings = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "executeWaitings",
      operationName: "executeWaitings",
      operationReturnType: "ExecuteWaitings",
      namespace: null,
      variables: {}
    });
    /** Executes the executeWebhookNode global action. */
    this.executeWebhookNode = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "executeWebhookNode",
      operationName: "executeWebhookNode",
      operationReturnType: "ExecuteWebhookNode",
      namespace: null,
      variables: {
        nodeId: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        contactNumber: { required: false, type: "String" },
        abandonedCheckoutUrl: { required: false, type: "String" }
      }
    });
    /** Executes the executeWebhookNodeRequest global action. */
    this.executeWebhookNodeRequest = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "executeWebhookNodeRequest",
      operationName: "executeWebhookNodeRequest",
      operationReturnType: "ExecuteWebhookNodeRequest",
      namespace: null,
      variables: {
        method: { required: false, type: "String" },
        url: { required: false, type: "String" },
        headers: { required: false, type: "String" },
        queryParams: { required: false, type: "String" },
        pathParams: { required: false, type: "String" },
        configResponse: { required: false, type: "String" },
        body: { required: false, type: "String" },
        contactNumber: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        isTest: { required: false, type: "Boolean" }
      }
    });
    /** Executes the exportAllContacts global action. */
    this.exportAllContacts = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "exportAllContacts",
      operationName: "exportAllContacts",
      operationReturnType: "ExportAllContacts",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        filters: { required: false, type: "[JSONObject!]" }
      }
    });
    /** Executes the exportAllConversations global action. */
    this.exportAllConversations = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "exportAllConversations",
      operationName: "exportAllConversations",
      operationReturnType: "ExportAllConversations",
      namespace: null,
      variables: { customerId: { required: false, type: "String" } }
    });
    /** Executes the exportAllCustomersCampaigns global action. */
    this.exportAllCustomersCampaigns = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "exportAllCustomersCampaigns",
      operationName: "exportAllCustomersCampaigns",
      operationReturnType: "ExportAllCustomersCampaigns",
      namespace: null,
      variables: {}
    });
    /** Executes the exportCampaignStats global action. */
    this.exportCampaignStats = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "exportCampaignStats",
      operationName: "exportCampaignStats",
      operationReturnType: "ExportCampaignStats",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        campaignId: { required: false, type: "String" },
        scenarioId: { required: false, type: "String" },
        exportFormat: { required: false, type: "String" },
        addStats: { required: false, type: "Boolean" },
        addContactDetails: { required: false, type: "Boolean" },
        addCustomFields: { required: false, type: "Boolean" },
        addButtonsClicks: { required: false, type: "Boolean" },
        dateFilter: { required: false, type: "String" },
        startDate: { required: false, type: "String" },
        endDate: { required: false, type: "String" }
      }
    });
    /** Executes the exportCampaignsBySendingTime global action. */
    this.exportCampaignsBySendingTime = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "exportCampaignsBySendingTime",
      operationName: "exportCampaignsBySendingTime",
      operationReturnType: "ExportCampaignsBySendingTime",
      namespace: null,
      variables: {}
    });
    /** Executes the exportCampaignsByTimeSlots global action. */
    this.exportCampaignsByTimeSlots = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "exportCampaignsByTimeSlots",
      operationName: "exportCampaignsByTimeSlots",
      operationReturnType: "ExportCampaignsByTimeSlots",
      namespace: null,
      variables: {}
    });
    /** Executes the exportContactsAsCsv global action. */
    this.exportContactsAsCsv = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "exportContactsAsCsv",
      operationName: "exportContactsAsCsv",
      operationReturnType: "ExportContactsAsCsv",
      namespace: null,
      variables: {}
    });
    /** Executes the exportContactsToSFTP global action. */
    this.exportContactsToSFTP = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "exportContactsToSFTP",
      operationName: "exportContactsToSFTP",
      operationReturnType: "ExportContactsToSFTP",
      namespace: null,
      variables: { customerId: { required: false, type: "String" } }
    });
    /** Executes the exportCustomerListToGoogleSheet global action. */
    this.exportCustomerListToGoogleSheet = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "exportCustomerListToGoogleSheet",
      operationName: "exportCustomerListToGoogleSheet",
      operationReturnType: "ExportCustomerListToGoogleSheet",
      namespace: null,
      variables: { cursor: { required: false, type: "String" } }
    });
    /** Executes the exportDataAllCampaignsToGoogleSheet global action. */
    this.exportDataAllCampaignsToGoogleSheet = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "exportDataAllCampaignsToGoogleSheet",
      operationName: "exportDataAllCampaignsToGoogleSheet",
      operationReturnType: "ExportDataAllCampaignsToGoogleSheet",
      namespace: null,
      variables: {}
    });
    /** Executes the exportLastMonthTurnover global action. */
    this.exportLastMonthTurnover = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "exportLastMonthTurnover",
      operationName: "exportLastMonthTurnover",
      operationReturnType: "ExportLastMonthTurnover",
      namespace: null,
      variables: {}
    });
    /** Executes the exportNonOptinInboundMessages global action. */
    this.exportNonOptinInboundMessages = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "exportNonOptinInboundMessages",
      operationName: "exportNonOptinInboundMessages",
      operationReturnType: "ExportNonOptinInboundMessages",
      namespace: null,
      variables: { customerId: { required: false, type: "String" } }
    });
    /** Executes the exportOptinContactsToGoogleSheet global action. */
    this.exportOptinContactsToGoogleSheet = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "exportOptinContactsToGoogleSheet",
      operationName: "exportOptinContactsToGoogleSheet",
      operationReturnType: "ExportOptinContactsToGoogleSheet",
      namespace: null,
      variables: { customerId: { required: false, type: "String" } }
    });
    /** Executes the exportSamsungAllContacts global action. */
    this.exportSamsungAllContacts = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "exportSamsungAllContacts",
      operationName: "exportSamsungAllContacts",
      operationReturnType: "ExportSamsungAllContacts",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        uploadToBucket: { required: false, type: "Boolean" },
        filters: { required: false, type: "[JSONObject!]" },
        contactCursor: { required: false, type: "String" },
        filePartNumber: { required: false, type: "Float" }
      }
    });
    /** Executes the exportSamsungCampaignClicks global action. */
    this.exportSamsungCampaignClicks = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "exportSamsungCampaignClicks",
      operationName: "exportSamsungCampaignClicks",
      operationReturnType: "ExportSamsungCampaignClicks",
      namespace: null,
      variables: {
        traceId: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        campaignId: { required: false, type: "String" },
        dryMode: { required: false, type: "Boolean" },
        startDate: { required: false, type: "String" },
        endDate: { required: false, type: "String" }
      }
    });
    /** Executes the exportSamsungCampaignClicksTest global action. */
    this.exportSamsungCampaignClicksTest = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "exportSamsungCampaignClicksTest",
      operationName: "exportSamsungCampaignClicksTest",
      operationReturnType: "ExportSamsungCampaignClicksTest",
      namespace: null,
      variables: {
        traceId: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        campaignId: { required: false, type: "String" },
        dryMode: { required: false, type: "Boolean" },
        startDate: { required: false, type: "String" },
        endDate: { required: false, type: "String" }
      }
    });
    /** Executes the exportSamsungCampaignSendings global action. */
    this.exportSamsungCampaignSendings = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "exportSamsungCampaignSendings",
      operationName: "exportSamsungCampaignSendings",
      operationReturnType: "ExportSamsungCampaignSendings",
      namespace: null,
      variables: {
        traceId: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        campaignId: { required: false, type: "String" },
        dryMode: { required: false, type: "Boolean" },
        startDate: { required: false, type: "String" },
        endDate: { required: false, type: "String" }
      }
    });
    /** Executes the exportSamsungCampaignSendingsTest global action. */
    this.exportSamsungCampaignSendingsTest = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "exportSamsungCampaignSendingsTest",
      operationName: "exportSamsungCampaignSendingsTest",
      operationReturnType: "ExportSamsungCampaignSendingsTest",
      namespace: null,
      variables: {
        traceId: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        campaignId: { required: false, type: "String" },
        dryMode: { required: false, type: "Boolean" },
        startDate: { required: false, type: "String" },
        endDate: { required: false, type: "String" }
      }
    });
    /** Executes the exportSamsungDailySendings global action. */
    this.exportSamsungDailySendings = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "exportSamsungDailySendings",
      operationName: "exportSamsungDailySendings",
      operationReturnType: "ExportSamsungDailySendings",
      namespace: null,
      variables: {
        traceId: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        uploadToBucket: { required: false, type: "Boolean" }
      }
    });
    /** Executes the exportSamsungDailySendings2 global action. */
    this.exportSamsungDailySendings2 = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "exportSamsungDailySendings2",
      operationName: "exportSamsungDailySendings2",
      operationReturnType: "ExportSamsungDailySendings2",
      namespace: null,
      variables: {
        traceId: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        dryMode: { required: false, type: "Boolean" }
      }
    });
    /** Executes the exportSamsungDailySendings2Test global action. */
    this.exportSamsungDailySendings2Test = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "exportSamsungDailySendings2Test",
      operationName: "exportSamsungDailySendings2Test",
      operationReturnType: "ExportSamsungDailySendings2Test",
      namespace: null,
      variables: {
        traceId: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        dryMode: { required: false, type: "Boolean" }
      }
    });
    /** Executes the exportSamsungParrainageCoupons global action. */
    this.exportSamsungParrainageCoupons = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "exportSamsungParrainageCoupons",
      operationName: "exportSamsungParrainageCoupons",
      operationReturnType: "ExportSamsungParrainageCoupons",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        uploadToBucket: { required: false, type: "Boolean" }
      }
    });
    /** Executes the exportTrackingToSFTP global action. */
    this.exportTrackingToSFTP = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "exportTrackingToSFTP",
      operationName: "exportTrackingToSFTP",
      operationReturnType: "ExportTrackingToSFTP",
      namespace: null,
      variables: { customerId: { required: false, type: "String" } }
    });
    /** Executes the extractContactTagNumbers global action. */
    this.extractContactTagNumbers = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "extractContactTagNumbers",
      operationName: "extractContactTagNumbers",
      operationReturnType: "ExtractContactTagNumbers",
      namespace: null,
      variables: {}
    });
    /** Executes the extractNumbersThatFailedCampaign global action. */
    this.extractNumbersThatFailedCampaign = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "extractNumbersThatFailedCampaign",
      operationName: "extractNumbersThatFailedCampaign",
      operationReturnType: "ExtractNumbersThatFailedCampaign",
      namespace: null,
      variables: { campaignId: { required: false, type: "String" } }
    });
    /** Executes the extractNumbersThatReadCampaign global action. */
    this.extractNumbersThatReadCampaign = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "extractNumbersThatReadCampaign",
      operationName: "extractNumbersThatReadCampaign",
      operationReturnType: "ExtractNumbersThatReadCampaign",
      namespace: null,
      variables: { campaignId: { required: false, type: "String" } }
    });
    /** Executes the extractNumbersThatReceivedCampaign global action. */
    this.extractNumbersThatReceivedCampaign = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "extractNumbersThatReceivedCampaign",
      operationName: "extractNumbersThatReceivedCampaign",
      operationReturnType: "ExtractNumbersThatReceivedCampaign",
      namespace: null,
      variables: { campaignId: { required: false, type: "String" } }
    });
    /** Executes the extractOptInContacts global action. */
    this.extractOptInContacts = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "extractOptInContacts",
      operationName: "extractOptInContacts",
      operationReturnType: "ExtractOptInContacts",
      namespace: null,
      variables: {}
    });
    /** Executes the extractOptOutContacts global action. */
    this.extractOptOutContacts = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "extractOptOutContacts",
      operationName: "extractOptOutContacts",
      operationReturnType: "ExtractOptOutContacts",
      namespace: null,
      variables: {}
    });
    /** Executes the extractOptinsThatReadMessage global action. */
    this.extractOptinsThatReadMessage = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "extractOptinsThatReadMessage",
      operationName: "extractOptinsThatReadMessage",
      operationReturnType: "ExtractOptinsThatReadMessage",
      namespace: null,
      variables: { customerId: { required: false, type: "String" } }
    });
    /** Executes the extractOrdersAttributedToCampaign global action. */
    this.extractOrdersAttributedToCampaign = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "extractOrdersAttributedToCampaign",
      operationName: "extractOrdersAttributedToCampaign",
      operationReturnType: "ExtractOrdersAttributedToCampaign",
      namespace: null,
      variables: { campaignId: { required: false, type: "String" } }
    });
    /** Executes the findSharedScenario global action. */
    this.findSharedScenario = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "findSharedScenario",
      operationName: "findSharedScenario",
      operationReturnType: "FindSharedScenario",
      namespace: null,
      variables: { shareCode: { required: false, type: "String" } }
    });
    /** Executes the findUserByResetPasswordToken global action. */
    this.findUserByResetPasswordToken = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "findUserByResetPasswordToken",
      operationName: "findUserByResetPasswordToken",
      operationReturnType: "FindUserByResetPasswordToken",
      namespace: null,
      variables: { resetPasswordToken: { required: false, type: "String" } }
    });
    /** Executes the findUserIdByEmail global action. */
    this.findUserIdByEmail = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "findUserIdByEmail",
      operationName: "findUserIdByEmail",
      operationReturnType: "FindUserIdByEmail",
      namespace: null,
      variables: { email: { required: false, type: "String" } }
    });
    /** Executes the finishEmbeddedSignup global action. */
    this.finishEmbeddedSignup = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "finishEmbeddedSignup",
      operationName: "finishEmbeddedSignup",
      operationReturnType: "FinishEmbeddedSignup",
      namespace: null,
      variables: {
        accessToken: { required: false, type: "String" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the generateCopilotAnswer global action. */
    this.generateCopilotAnswer = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "generateCopilotAnswer",
      operationName: "generateCopilotAnswer",
      operationReturnType: "GenerateCopilotAnswer",
      namespace: null,
      variables: {
        conversationId: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        contactId: { required: false, type: "String" }
      }
    });
    /** Executes the generateFiltersFromTextWithAI global action. */
    this.generateFiltersFromTextWithAI = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "generateFiltersFromTextWithAI",
      operationName: "generateFiltersFromTextWithAI",
      operationReturnType: "GenerateFiltersFromTextWithAI",
      namespace: null,
      variables: {
        text: { required: false, type: "String" },
        customFields: { required: false, type: "JSONObject" },
        tags: { required: false, type: "[JSONObject!]" },
        recentCampaigns: { required: false, type: "[JSONObject!]" },
        activeAutomations: { required: false, type: "[JSONObject!]" },
        chatbots: { required: false, type: "[JSONObject!]" },
        integrations: { required: false, type: "JSONObject" }
      }
    });
    /** Executes the generateGaiaWebsiteDatasource global action. */
    this.generateGaiaWebsiteDatasource = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "generateGaiaWebsiteDatasource",
      operationName: "generateGaiaWebsiteDatasource",
      operationReturnType: "GenerateGaiaWebsiteDatasource",
      namespace: null,
      variables: {
        gaiaWebsiteDatasources: {
          required: false,
          type: "[GenerateGaiaWebsiteDatasourceGaiaWebsiteDatasourcesElementTypeInput!]"
        },
        title: { required: false, type: "String" }
      }
    });
    /** Executes the generateJustCheckout global action. */
    this.generateJustCheckout = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "generateJustCheckout",
      operationName: "generateJustCheckout",
      operationReturnType: "GenerateJustCheckout",
      namespace: null,
      variables: {
        shopifyCheckoutId: { required: false, type: "String" },
        phoneNumber: { required: false, type: "String" }
      }
    });
    /** Executes the generateKlaviyoInstallationUrl global action. */
    this.generateKlaviyoInstallationUrl = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "generateKlaviyoInstallationUrl",
      operationName: "generateKlaviyoInstallationUrl",
      operationReturnType: "GenerateKlaviyoInstallationUrl",
      namespace: null,
      variables: { customerId: { required: false, type: "String" } }
    });
    /** Executes the get360DialogMessagingLimit global action. */
    this.get360DialogMessagingLimit = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "get360DialogMessagingLimit",
      operationName: "get360DialogMessagingLimit",
      operationReturnType: "Get360DialogMessagingLimit",
      namespace: null,
      variables: { customerId: { required: false, type: "String" } }
    });
    /** Executes the getAlcmeonInMessageJWT global action. */
    this.getAlcmeonInMessageJWT = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getAlcmeonInMessageJWT",
      operationName: "getAlcmeonInMessageJWT",
      operationReturnType: "GetAlcmeonInMessageJWT",
      namespace: null,
      variables: {
        data: { required: false, type: "GetAlcmeonInMessageJWTDataInput" }
      }
    });
    /** Executes the getAllContacts global action. */
    this.getAllContacts = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getAllContacts",
      operationName: "getAllContacts",
      operationReturnType: "GetAllContacts",
      namespace: null,
      variables: { isShopifyApp: { required: false, type: "Boolean" } }
    });
    /** Executes the getAllUrlClickEventBetweenDates global action. */
    this.getAllUrlClickEventBetweenDates = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getAllUrlClickEventBetweenDates",
      operationName: "getAllUrlClickEventBetweenDates",
      operationReturnType: "GetAllUrlClickEventBetweenDates",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        scenarioId: { required: false, type: "String" },
        startDate: { required: false, type: "String" },
        endDate: { required: false, type: "String" }
      }
    });
    /** Executes the getAllUsers global action. */
    this.getAllUsers = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getAllUsers",
      operationName: "getAllUsers",
      operationReturnType: "GetAllUsers",
      namespace: null,
      variables: {}
    });
    /** Executes the getButtonClickNumber global action. */
    this.getButtonClickNumber = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getButtonClickNumber",
      operationName: "getButtonClickNumber",
      operationReturnType: "GetButtonClickNumber",
      namespace: null,
      variables: { replyButtonId: { required: false, type: "String" } }
    });
    /** Executes the getCampaignClicksBetweenDates global action. */
    this.getCampaignClicksBetweenDates = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getCampaignClicksBetweenDates",
      operationName: "getCampaignClicksBetweenDates",
      operationReturnType: "GetCampaignClicksBetweenDates",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        replyButtonIds: { required: false, type: "String" },
        startDate: { required: false, type: "String" },
        endDate: { required: false, type: "String" }
      }
    });
    /** Executes the getCampaignFailedReasons global action. */
    this.getCampaignFailedReasons = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getCampaignFailedReasons",
      operationName: "getCampaignFailedReasons",
      operationReturnType: "GetCampaignFailedReasons",
      namespace: null,
      variables: { campaignId: { required: false, type: "String" } }
    });
    /** Executes the getCampaignStatsBetweenDates global action. */
    this.getCampaignStatsBetweenDates = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getCampaignStatsBetweenDates",
      operationName: "getCampaignStatsBetweenDates",
      operationReturnType: "GetCampaignStatsBetweenDates",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        campaignId: { required: false, type: "String" },
        scenarioId: { required: false, type: "String" },
        startDate: { required: false, type: "String" },
        endDate: { required: false, type: "String" }
      }
    });
    /** Executes the getCampaignStatsTest global action. */
    this.getCampaignStatsTest = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getCampaignStatsTest",
      operationName: "getCampaignStatsTest",
      operationReturnType: "GetCampaignStatsTest",
      namespace: null,
      variables: {}
    });
    /** Executes the getCampaignStatuses global action. */
    this.getCampaignStatuses = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getCampaignStatuses",
      operationName: "getCampaignStatuses",
      operationReturnType: "GetCampaignStatuses",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        campaignIds: { required: false, type: "[String!]" }
      }
    });
    /** Executes the getCampaignsDataExport global action. */
    this.getCampaignsDataExport = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getCampaignsDataExport",
      operationName: "getCampaignsDataExport",
      operationReturnType: "GetCampaignsDataExport",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        startDate: { required: false, type: "String" },
        endDate: { required: false, type: "String" },
        currentDate: { required: false, type: "String" },
        accData: { required: false, type: "String" },
        exportMethod: { required: false, type: "String" },
        sheetId: { required: false, type: "String" },
        isShopifyCustomer: { required: false, type: "Boolean" }
      }
    });
    /** Executes the getContactStats global action. */
    this.getContactStats = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getContactStats",
      operationName: "getContactStats",
      operationReturnType: "GetContactStats",
      namespace: null,
      variables: { contactId: { required: false, type: "String" } }
    });
    /** Executes the getContactsBreakdown global action. */
    this.getContactsBreakdown = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getContactsBreakdown",
      operationName: "getContactsBreakdown",
      operationReturnType: "GetContactsBreakdown",
      namespace: null,
      variables: { contactList: { required: false, type: "[String!]" } }
    });
    /** Executes the getConversationsConsumedThisMonth global action. */
    this.getConversationsConsumedThisMonth = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getConversationsConsumedThisMonth",
      operationName: "getConversationsConsumedThisMonth",
      operationReturnType: "GetConversationsConsumedThisMonth",
      namespace: null,
      variables: { customerId: { required: false, type: "String" } }
    });
    /** Executes the getCustomerStatsBetweenDates global action. */
    this.getCustomerStatsBetweenDates = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getCustomerStatsBetweenDates",
      operationName: "getCustomerStatsBetweenDates",
      operationReturnType: "GetCustomerStatsBetweenDates",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        shopId: { required: false, type: "String" },
        startDate: { required: false, type: "String" },
        endDate: { required: false, type: "String" }
      }
    });
    /** Executes the getDuplicates global action. */
    this.getDuplicates = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getDuplicates",
      operationName: "getDuplicates",
      operationReturnType: "GetDuplicates",
      namespace: null,
      variables: {}
    });
    /** Executes the getFlowNodes global action. */
    this.getFlowNodes = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getFlowNodes",
      operationName: "getFlowNodes",
      operationReturnType: "GetFlowNodes",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        scenarioId: { required: false, type: "String" }
      }
    });
    /** Executes the getHubspotContacts global action. */
    this.getHubspotContacts = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getHubspotContacts",
      operationName: "getHubspotContacts",
      operationReturnType: "GetHubspotContacts",
      namespace: null,
      variables: {
        listId: { required: false, type: "String" },
        apiKeyHubspot: { required: false, type: "String" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the getHubspotLists global action. */
    this.getHubspotLists = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getHubspotLists",
      operationName: "getHubspotLists",
      operationReturnType: "GetHubspotLists",
      namespace: null,
      variables: { apiKeyHubspot: { required: false, type: "String" } }
    });
    /** Executes the getKlaviyoProfiles global action. */
    this.getKlaviyoProfiles = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getKlaviyoProfiles",
      operationName: "getKlaviyoProfiles",
      operationReturnType: "GetKlaviyoProfiles",
      namespace: null,
      variables: {
        segmentId: { required: false, type: "String" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the getOptOutVWweird global action. */
    this.getOptOutVWweird = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getOptOutVWweird",
      operationName: "getOptOutVWweird",
      operationReturnType: "GetOptOutVWweird",
      namespace: null,
      variables: {}
    });
    /** Executes the getOptinStatusFromShopify global action. */
    this.getOptinStatusFromShopify = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getOptinStatusFromShopify",
      operationName: "getOptinStatusFromShopify",
      operationReturnType: "GetOptinStatusFromShopify",
      namespace: null,
      variables: {}
    });
    /** Executes the getOrCreateContact global action. */
    this.getOrCreateContact = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getOrCreateContact",
      operationName: "getOrCreateContact",
      operationReturnType: "GetOrCreateContact",
      namespace: null,
      variables: {
        fromNumber: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        contactName: { required: false, type: "String" }
      }
    });
    /** Executes the getOrCreateConversation global action. */
    this.getOrCreateConversation = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getOrCreateConversation",
      operationName: "getOrCreateConversation",
      operationReturnType: "GetOrCreateConversation",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        contactId: { required: false, type: "String" },
        attributedUserId: { required: false, type: "String" }
      }
    });
    /** Executes the getScenarioShareableLink global action. */
    this.getScenarioShareableLink = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getScenarioShareableLink",
      operationName: "getScenarioShareableLink",
      operationReturnType: "GetScenarioShareableLink",
      namespace: null,
      variables: { scenarioId: { required: false, type: "String" } }
    });
    /** Executes the getShopifyCustomers global action. */
    this.getShopifyCustomers = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getShopifyCustomers",
      operationName: "getShopifyCustomers",
      operationReturnType: "GetShopifyCustomers",
      namespace: null,
      variables: {}
    });
    /** Executes the getShopifyOrders global action. */
    this.getShopifyOrders = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getShopifyOrders",
      operationName: "getShopifyOrders",
      operationReturnType: "GetShopifyOrders",
      namespace: null,
      variables: {}
    });
    /** Executes the getShopifySegmentCustomers global action. */
    this.getShopifySegmentCustomers = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getShopifySegmentCustomers",
      operationName: "getShopifySegmentCustomers",
      operationReturnType: "GetShopifySegmentCustomers",
      namespace: null,
      variables: { segmentQuery: { required: false, type: "String" } }
    });
    /** Executes the getShopifySegments global action. */
    this.getShopifySegments = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getShopifySegments",
      operationName: "getShopifySegments",
      operationReturnType: "GetShopifySegments",
      namespace: null,
      variables: {}
    });
    /** Executes the getSimioSegmentContacts global action. */
    this.getSimioSegmentContacts = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getSimioSegmentContacts",
      operationName: "getSimioSegmentContacts",
      operationReturnType: "GetSimioSegmentContacts",
      namespace: null,
      variables: {
        segmentId: { required: false, type: "String" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the getSplioOrders global action. */
    this.getSplioOrders = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getSplioOrders",
      operationName: "getSplioOrders",
      operationReturnType: "GetSplioOrders",
      namespace: null,
      variables: {
        campaignId: { required: false, type: "String" },
        splioToken: { required: false, type: "String" }
      }
    });
    /** Executes the getStripeInvoices global action. */
    this.getStripeInvoices = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getStripeInvoices",
      operationName: "getStripeInvoices",
      operationReturnType: "GetStripeInvoices",
      namespace: null,
      variables: { customerId: { required: false, type: "String" } }
    });
    /** Executes the getUserCustomer global action. */
    this.getUserCustomer = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getUserCustomer",
      operationName: "getUserCustomer",
      operationReturnType: "GetUserCustomer",
      namespace: null,
      variables: {}
    });
    /** Executes the getVectorEmbedding global action. */
    this.getVectorEmbedding = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getVectorEmbedding",
      operationName: "getVectorEmbedding",
      operationReturnType: "GetVectorEmbedding",
      namespace: null,
      variables: { text: { required: false, type: "String" } }
    });
    /** Executes the getWhatsAppMessagingLimitTier global action. */
    this.getWhatsAppMessagingLimitTier = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getWhatsAppMessagingLimitTier",
      operationName: "getWhatsAppMessagingLimitTier",
      operationReturnType: "GetWhatsAppMessagingLimitTier",
      namespace: null,
      variables: { customerId: { required: false, type: "String" } }
    });
    /** Executes the getWhatsAppTemplates global action. */
    this.getWhatsAppTemplates = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getWhatsAppTemplates",
      operationName: "getWhatsAppTemplates",
      operationReturnType: "GetWhatsAppTemplates",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        limit: { required: false, type: "String" },
        templateName: { required: false, type: "String" }
      }
    });
    /** Executes the globalShopifySync global action. */
    this.globalShopifySync = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "globalShopifySync",
      operationName: "globalShopifySync",
      operationReturnType: "GlobalShopifySync",
      namespace: null,
      variables: {
        apiKeys: { required: false, type: "[String!]" },
        syncSince: { required: false, type: "DateTime" },
        models: { required: false, type: "[String!]" },
        force: { required: false, type: "Boolean" },
        startReason: { required: false, type: "String" }
      }
    });
    /** Executes the handleGorgiasTicketing global action. */
    this.handleGorgiasTicketing = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "handleGorgiasTicketing",
      operationName: "handleGorgiasTicketing",
      operationReturnType: "HandleGorgiasTicketing",
      namespace: null,
      variables: {
        conversationId: { required: false, type: "String" },
        messageId: { required: false, type: "String" },
        messageContent: { required: false, type: "String" },
        messageType: { required: false, type: "String" },
        messageDirection: { required: false, type: "String" },
        imageUrl: { required: false, type: "String" },
        videoUrl: { required: false, type: "String" },
        voiceUrl: { required: false, type: "String" },
        documentUrl: { required: false, type: "String" },
        caption: { required: false, type: "String" }
      }
    });
    /** Executes the importContactListFromSFTP global action. */
    this.importContactListFromSFTP = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "importContactListFromSFTP",
      operationName: "importContactListFromSFTP",
      operationReturnType: "ImportContactListFromSFTP",
      namespace: null,
      variables: { customerId: { required: false, type: "String" } }
    });
    /** Executes the importContactsManually global action. */
    this.importContactsManually = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "importContactsManually",
      operationName: "importContactsManually",
      operationReturnType: "ImportContactsManually",
      namespace: null,
      variables: {}
    });
    /** Executes the iterateSamsungDailySendings global action. */
    this.iterateSamsungDailySendings = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "iterateSamsungDailySendings",
      operationName: "iterateSamsungDailySendings",
      operationReturnType: "IterateSamsungDailySendings",
      namespace: null,
      variables: {
        traceId: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        remainingCampaigns: { required: false, type: "String" },
        data: { required: false, type: "String" },
        uploadToBucket: { required: false, type: "Boolean" },
        campaignCursor: { required: false, type: "String" }
      }
    });
    /** Executes the iterateSamsungUrlClicks global action. */
    this.iterateSamsungUrlClicks = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "iterateSamsungUrlClicks",
      operationName: "iterateSamsungUrlClicks",
      operationReturnType: "IterateSamsungUrlClicks",
      namespace: null,
      variables: {
        traceId: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        remainingCampaigns: { required: false, type: "String" },
        data: { required: false, type: "String" },
        uploadToBucket: { required: false, type: "Boolean" },
        sendingsCursor: { required: false, type: "String" },
        startDate: { required: false, type: "String" },
        endDate: { required: false, type: "String" },
        retryCount: { required: false, type: "String" }
      }
    });
    /** Executes the iterateTrackingExport global action. */
    this.iterateTrackingExport = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "iterateTrackingExport",
      operationName: "iterateTrackingExport",
      operationReturnType: "IterateTrackingExport",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        remainingCampaigns: { required: false, type: "String" },
        data: { required: false, type: "String" },
        uploadToBucket: { required: false, type: "Boolean" },
        campaignCursor: { required: false, type: "String" }
      }
    });
    /** Executes the launchAiChatbot global action. */
    this.launchAiChatbot = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "launchAiChatbot",
      operationName: "launchAiChatbot",
      operationReturnType: "LaunchAiChatbot",
      namespace: null,
      variables: {
        nodeId: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        contactNumber: { required: false, type: "String" }
      }
    });
    /** Executes the listFilesFromCustomerSFTP global action. */
    this.listFilesFromCustomerSFTP = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "listFilesFromCustomerSFTP",
      operationName: "listFilesFromCustomerSFTP",
      operationReturnType: "ListFilesFromCustomerSFTP",
      namespace: null,
      variables: { customerId: { required: false, type: "String" } }
    });
    /** Executes the listFilesOnS3Bucket global action. */
    this.listFilesOnS3Bucket = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "listFilesOnS3Bucket",
      operationName: "listFilesOnS3Bucket",
      operationReturnType: "ListFilesOnS3Bucket",
      namespace: null,
      variables: { bucketName: { required: false, type: "String" } }
    });
    /** Executes the manuallySendBackInStockAlerts global action. */
    this.manuallySendBackInStockAlerts = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "manuallySendBackInStockAlerts",
      operationName: "manuallySendBackInStockAlerts",
      operationReturnType: "ManuallySendBackInStockAlerts",
      namespace: null,
      variables: {}
    });
    /** Executes the manuallySendCampaign global action. */
    this.manuallySendCampaign = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "manuallySendCampaign",
      operationName: "manuallySendCampaign",
      operationReturnType: "ManuallySendCampaign",
      namespace: null,
      variables: { contactList: { required: false, type: "String" } }
    });
    /** Executes the manuallySyncKlaviyoProfilesFromSegment global action. */
    this.manuallySyncKlaviyoProfilesFromSegment = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "manuallySyncKlaviyoProfilesFromSegment",
      operationName: "manuallySyncKlaviyoProfilesFromSegment",
      operationReturnType: "ManuallySyncKlaviyoProfilesFromSegment",
      namespace: null,
      variables: { customerId: { required: false, type: "String" } }
    });
    /** Executes the manuallyUpdateBisAlertsStatus global action. */
    this.manuallyUpdateBisAlertsStatus = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "manuallyUpdateBisAlertsStatus",
      operationName: "manuallyUpdateBisAlertsStatus",
      operationReturnType: "ManuallyUpdateBisAlertsStatus",
      namespace: null,
      variables: {}
    });
    /** Executes the mergeGorgiasCustomers global action. */
    this.mergeGorgiasCustomers = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "mergeGorgiasCustomers",
      operationName: "mergeGorgiasCustomers",
      operationReturnType: "MergeGorgiasCustomers",
      namespace: null,
      variables: {}
    });
    /** Executes the mergeTags global action. */
    this.mergeTags = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "mergeTags",
      operationName: "mergeTags",
      operationReturnType: "MergeTags",
      namespace: null,
      variables: {
        mergedTagId: { required: false, type: "String" },
        targetTagId: { required: false, type: "String" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the migrateMessageContacts global action. */
    this.migrateMessageContacts = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "migrateMessageContacts",
      operationName: "migrateMessageContacts",
      operationReturnType: "MigrateMessageContacts",
      namespace: null,
      variables: { batchSize: { required: false, type: "Float" } }
    });
    /** Executes the newImageMessage global action. */
    this.newImageMessage = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "newImageMessage",
      operationName: "newImageMessage",
      operationReturnType: "NewImageMessage",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        fromNumber: { required: false, type: "String" },
        contactName: { required: false, type: "String" },
        imageId: { required: false, type: "String" },
        caption: { required: false, type: "String" },
        whatsappMessageId: { required: false, type: "String" },
        bsuid: { required: false, type: "String" }
      }
    });
    /** Executes the newOptinFormCompletion global action. */
    this.newOptinFormCompletion = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "newOptinFormCompletion",
      operationName: "newOptinFormCompletion",
      operationReturnType: "NewOptinFormCompletion",
      namespace: null,
      variables: {
        optinFormCompletionId: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        phone: { required: false, type: "String" },
        data: { required: false, type: "String" },
        formName: { required: false, type: "String" }
      }
    });
    /** Executes the newSamsungFormOptin global action. */
    this.newSamsungFormOptin = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "newSamsungFormOptin",
      operationName: "newSamsungFormOptin",
      operationReturnType: "NewSamsungFormOptin",
      namespace: null,
      variables: {
        samsungFormId: { required: false, type: "String" },
        phone: { required: false, type: "String" },
        customProperties: { required: false, type: "String" },
        d_dpuuid: { required: false, type: "String" },
        origin: { required: false, type: "String" }
      }
    });
    /** Executes the newTextMessage global action. */
    this.newTextMessage = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "newTextMessage",
      operationName: "newTextMessage",
      operationReturnType: "NewTextMessage",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        fromNumber: { required: false, type: "String" },
        contactName: { required: false, type: "String" },
        body: { required: false, type: "String" },
        whatsappMessageId: { required: false, type: "String" }
      }
    });
    /** Executes the newVideoMessage global action. */
    this.newVideoMessage = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "newVideoMessage",
      operationName: "newVideoMessage",
      operationReturnType: "NewVideoMessage",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        fromNumber: { required: false, type: "String" },
        contactName: { required: false, type: "String" },
        videoId: { required: false, type: "String" },
        caption: { required: false, type: "String" },
        whatsappMessageId: { required: false, type: "String" },
        bsuid: { required: false, type: "String" }
      }
    });
    /** Executes the newVoiceMessage global action. */
    this.newVoiceMessage = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "newVoiceMessage",
      operationName: "newVoiceMessage",
      operationReturnType: "NewVoiceMessage",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        fromNumber: { required: false, type: "String" },
        contactName: { required: false, type: "String" },
        voiceId: { required: false, type: "String" },
        whatsappMessageId: { required: false, type: "String" },
        bsuid: { required: false, type: "String" }
      }
    });
    /** Executes the onContactTagSuccess global action. */
    this.onContactTagSuccess = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "onContactTagSuccess",
      operationName: "onContactTagSuccess",
      operationReturnType: "OnContactTagSuccess",
      namespace: null,
      variables: {
        contactId: { required: false, type: "String" },
        tagId: { required: false, type: "String" }
      }
    });
    /** Executes the optInContact global action. */
    this.optInContact = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "optInContact",
      operationName: "optInContact",
      operationReturnType: "OptInContact",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        contactNumber: { required: false, type: "String" },
        scenarioId: { required: false, type: "String" },
        isCheckoutOptin: { required: false, type: "Boolean" },
        isKlaviyoOptin: { required: false, type: "Boolean" },
        klaviyoFirstName: { required: false, type: "String" },
        klaviyoLastName: { required: false, type: "String" },
        isPhoneNumberEncrypted: { required: false, type: "Boolean" }
      }
    });
    /** Executes the optInSourceBreakdown global action. */
    this.optInSourceBreakdown = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "optInSourceBreakdown",
      operationName: "optInSourceBreakdown",
      operationReturnType: "OptInSourceBreakdown",
      namespace: null,
      variables: {}
    });
    /** Executes the optOutContact global action. */
    this.optOutContact = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "optOutContact",
      operationName: "optOutContact",
      operationReturnType: "OptOutContact",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        contactNumber: { required: false, type: "String" },
        scenarioId: { required: false, type: "String" },
        isPhoneNumberEncrypted: { required: false, type: "Boolean" }
      }
    });
    /** Executes the optinAgainAllOptins global action. */
    this.optinAgainAllOptins = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "optinAgainAllOptins",
      operationName: "optinAgainAllOptins",
      operationReturnType: "OptinAgainAllOptins",
      namespace: null,
      variables: { customerId: { required: false, type: "String" } }
    });
    /** Executes the optoutInAppOptouts global action. */
    this.optoutInAppOptouts = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "optoutInAppOptouts",
      operationName: "optoutInAppOptouts",
      operationReturnType: "OptoutInAppOptouts",
      namespace: null,
      variables: {}
    });
    /** Executes the phoneNumberCorrection global action. */
    this.phoneNumberCorrection = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "phoneNumberCorrection",
      operationName: "phoneNumberCorrection",
      operationReturnType: "PhoneNumberCorrection",
      namespace: null,
      variables: {}
    });
    /** Executes the processEasiwareWebhook global action. */
    this.processEasiwareWebhook = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "processEasiwareWebhook",
      operationName: "processEasiwareWebhook",
      operationReturnType: "ProcessEasiwareWebhook",
      namespace: null,
      variables: { data: { required: false, type: "JSONObject" } }
    });
    /** Executes the processGorgiasWebhook global action. */
    this.processGorgiasWebhook = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "processGorgiasWebhook",
      operationName: "processGorgiasWebhook",
      operationReturnType: "ProcessGorgiasWebhook",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        ticketId: { required: false, type: "String" },
        message: { required: false, type: "String" },
        attachments: { required: false, type: "String" }
      }
    });
    /** Executes the processSamsungAutomationBatch global action. */
    this.processSamsungAutomationBatch = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "processSamsungAutomationBatch",
      operationName: "processSamsungAutomationBatch",
      operationReturnType: "ProcessSamsungAutomationBatch",
      namespace: null,
      variables: {
        traceId: { required: false, type: "String" },
        batchData: {
          required: false,
          type: "[ProcessSamsungAutomationBatchBatchDataElementTypeInput!]"
        },
        campaignId: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        simioOrigin: { required: false, type: "String" },
        alreadySentKeys: { required: false, type: "[String!]" },
        delayUntilNextDeliveryTimeInSeconds: { required: false, type: "Float" },
        batchIndex: { required: false, type: "Float" },
        batchSize: { required: false, type: "Float" }
      }
    });
    /** Executes the processWhatsAppOrder global action. */
    this.processWhatsAppOrder = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "processWhatsAppOrder",
      operationName: "processWhatsAppOrder",
      operationReturnType: "ProcessWhatsAppOrder",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        webhookData: { required: false, type: "JSONObject" },
        phoneNumber: { required: false, type: "String" }
      }
    });
    /** Executes the processWhatsappWebhookHistory global action. */
    this.processWhatsappWebhookHistory = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "processWhatsappWebhookHistory",
      operationName: "processWhatsappWebhookHistory",
      operationReturnType: "ProcessWhatsappWebhookHistory",
      namespace: null,
      variables: {}
    });
    /** Executes the processWhatsappWebhookHistoryFromTheDay global action. */
    this.processWhatsappWebhookHistoryFromTheDay = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "processWhatsappWebhookHistoryFromTheDay",
      operationName: "processWhatsappWebhookHistoryFromTheDay",
      operationReturnType: "ProcessWhatsappWebhookHistoryFromTheDay",
      namespace: null,
      variables: { customerId: { required: false, type: "String" } }
    });
    /** Executes the processWhatsappWebhookHistoryFromTheHour global action. */
    this.processWhatsappWebhookHistoryFromTheHour = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "processWhatsappWebhookHistoryFromTheHour",
      operationName: "processWhatsappWebhookHistoryFromTheHour",
      operationReturnType: "ProcessWhatsappWebhookHistoryFromTheHour",
      namespace: null,
      variables: {}
    });
    /** Executes the processZendeskWebhook global action. */
    this.processZendeskWebhook = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "processZendeskWebhook",
      operationName: "processZendeskWebhook",
      operationReturnType: "ProcessZendeskWebhook",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        ticketId: { required: false, type: "String" },
        message: { required: false, type: "String" },
        isTicketSolved: { required: false, type: "Boolean" },
        isTicketWaiting: { required: false, type: "Boolean" }
      }
    });
    /** Executes the putSamsungCampaignDataToBucketDaily global action. */
    this.putSamsungCampaignDataToBucketDaily = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "putSamsungCampaignDataToBucketDaily",
      operationName: "putSamsungCampaignDataToBucketDaily",
      operationReturnType: "PutSamsungCampaignDataToBucketDaily",
      namespace: null,
      variables: {}
    });
    /** Executes the putSamsungCampaignDataToBucketDaily2 global action. */
    this.putSamsungCampaignDataToBucketDaily2 = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "putSamsungCampaignDataToBucketDaily2",
      operationName: "putSamsungCampaignDataToBucketDaily2",
      operationReturnType: "PutSamsungCampaignDataToBucketDaily2",
      namespace: null,
      variables: {}
    });
    /** Executes the putSamsungCampaignDataToBucketDaily2Test global action. */
    this.putSamsungCampaignDataToBucketDaily2Test = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "putSamsungCampaignDataToBucketDaily2Test",
      operationName: "putSamsungCampaignDataToBucketDaily2Test",
      operationReturnType: "PutSamsungCampaignDataToBucketDaily2Test",
      namespace: null,
      variables: {}
    });
    /** Executes the putSamsungContactDataToBucketDaily global action. */
    this.putSamsungContactDataToBucketDaily = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "putSamsungContactDataToBucketDaily",
      operationName: "putSamsungContactDataToBucketDaily",
      operationReturnType: "PutSamsungContactDataToBucketDaily",
      namespace: null,
      variables: {}
    });
    /** Executes the putSamsungParrainageCouponsToBucketDaily global action. */
    this.putSamsungParrainageCouponsToBucketDaily = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "putSamsungParrainageCouponsToBucketDaily",
      operationName: "putSamsungParrainageCouponsToBucketDaily",
      operationReturnType: "PutSamsungParrainageCouponsToBucketDaily",
      namespace: null,
      variables: {}
    });
    /** Executes the rattrapageShopifyOrdersAttributionOctobre2025 global action. */
    this.rattrapageShopifyOrdersAttributionOctobre2025 = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "rattrapageShopifyOrdersAttributionOctobre2025",
      operationName: "rattrapageShopifyOrdersAttributionOctobre2025",
      operationReturnType: "RattrapageShopifyOrdersAttributionOctobre2025",
      namespace: null,
      variables: { startDate: { required: false, type: "String" } }
    });
    /** Executes the refreshAllSegments global action. */
    this.refreshAllSegments = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "refreshAllSegments",
      operationName: "refreshAllSegments",
      operationReturnType: "RefreshAllSegments",
      namespace: null,
      variables: {}
    });
    /** Executes the refreshFacebookAccessToken global action. */
    this.refreshFacebookAccessToken = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "refreshFacebookAccessToken",
      operationName: "refreshFacebookAccessToken",
      operationReturnType: "RefreshFacebookAccessToken",
      namespace: null,
      variables: {}
    });
    /** Executes the removeContactsFromSegment global action. */
    this.removeContactsFromSegment = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "removeContactsFromSegment",
      operationName: "removeContactsFromSegment",
      operationReturnType: "RemoveContactsFromSegment",
      namespace: null,
      variables: {
        segmentId: { required: false, type: "String" },
        contactIds: { required: false, type: "[String!]" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the rescheduleSendingsFromStopped global action. */
    this.rescheduleSendingsFromStopped = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "rescheduleSendingsFromStopped",
      operationName: "rescheduleSendingsFromStopped",
      operationReturnType: "RescheduleSendingsFromStopped",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        sendingBatchId: { required: false, type: "Float" },
        targetDate: { required: false, type: "String" },
        execute: { required: false, type: "Boolean" }
      }
    });
    /** Executes the resendCampaignForErrors global action. */
    this.resendCampaignForErrors = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "resendCampaignForErrors",
      operationName: "resendCampaignForErrors",
      operationReturnType: "ResendCampaignForErrors",
      namespace: null,
      variables: {
        campaignId: { required: false, type: "String" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the resetAllContactsOfCustomer global action. */
    this.resetAllContactsOfCustomer = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "resetAllContactsOfCustomer",
      operationName: "resetAllContactsOfCustomer",
      operationReturnType: "ResetAllContactsOfCustomer",
      namespace: null,
      variables: { customerId: { required: false, type: "String" } }
    });
    /** Executes the salesAiAnswer global action. */
    this.salesAiAnswer = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "salesAiAnswer",
      operationName: "salesAiAnswer",
      operationReturnType: "SalesAiAnswer",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        conversationId: { required: false, type: "String" },
        contactName: { required: false, type: "String" },
        contactNumber: { required: false, type: "String" }
      }
    });
    /** Executes the scheduleJacadiVoucherExpirationAutomation global action. */
    this.scheduleJacadiVoucherExpirationAutomation = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "scheduleJacadiVoucherExpirationAutomation",
      operationName: "scheduleJacadiVoucherExpirationAutomation",
      operationReturnType: "ScheduleJacadiVoucherExpirationAutomation",
      namespace: null,
      variables: {}
    });
    /** @deprecated The action scheduledShopifySync does not have an api trigger and cannot be called from this api client. If you are the developer of this application and want api clients to call this action add an api trigger to the action. For more information see: https://docs.gadget.dev/guides/actions/triggers */
    this.scheduledShopifySync = buildGlobalAction(this, {
      type: "stubbedAction",
      functionName: "scheduledShopifySync",
      actionApiIdentifier: "scheduledShopifySync",
      dataPath: "scheduledShopifySync",
      errorMessage: "The action scheduledShopifySync does not have an api trigger and cannot be called from this api client. If you are the developer of this application and want api clients to call this action add an api trigger to the action. For more information see: https://docs.gadget.dev/guides/actions/triggers",
      variables: {},
      reason: "MissingApiTrigger"
    });
    /** Executes the send360DialogFeesToStripe global action. */
    this.send360DialogFeesToStripe = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "send360DialogFeesToStripe",
      operationName: "send360DialogFeesToStripe",
      operationReturnType: "Send360DialogFeesToStripe",
      namespace: null,
      variables: { dryMode: { required: false, type: "Boolean" } }
    });
    /** Executes the sendBabaCartMessages global action. */
    this.sendBabaCartMessages = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "sendBabaCartMessages",
      operationName: "sendBabaCartMessages",
      operationReturnType: "SendBabaCartMessages",
      namespace: null,
      variables: { data: { required: false, type: "String" } }
    });
    /** Executes the sendEventToKlaviyo global action. */
    this.sendEventToKlaviyo = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "sendEventToKlaviyo",
      operationName: "sendEventToKlaviyo",
      operationReturnType: "SendEventToKlaviyo",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        userPhoneNumber: { required: false, type: "String" },
        campaignId: { required: false, type: "String" },
        eventName: { required: false, type: "String" }
      }
    });
    /** Executes the sendMessageDataToTinyBirdBatch global action. */
    this.sendMessageDataToTinyBirdBatch = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "sendMessageDataToTinyBirdBatch",
      operationName: "sendMessageDataToTinyBirdBatch",
      operationReturnType: "SendMessageDataToTinyBirdBatch",
      namespace: null,
      variables: { cursor: { required: false, type: "String" } }
    });
    /** Executes the sendMessageDataToTinybird global action. */
    this.sendMessageDataToTinybird = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "sendMessageDataToTinybird",
      operationName: "sendMessageDataToTinybird",
      operationReturnType: "SendMessageDataToTinybird",
      namespace: null,
      variables: {}
    });
    /** Executes the sendNumberOfOptinsToStripe global action. */
    this.sendNumberOfOptinsToStripe = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "sendNumberOfOptinsToStripe",
      operationName: "sendNumberOfOptinsToStripe",
      operationReturnType: "SendNumberOfOptinsToStripe",
      namespace: null,
      variables: { dryMode: { required: false, type: "Boolean" } }
    });
    /** Executes the sendNumberOfSendingsToStripe global action. */
    this.sendNumberOfSendingsToStripe = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "sendNumberOfSendingsToStripe",
      operationName: "sendNumberOfSendingsToStripe",
      operationReturnType: "SendNumberOfSendingsToStripe",
      namespace: null,
      variables: { dryMode: { required: false, type: "Boolean" } }
    });
    /** Executes the sendProgressiveBackInStockAlerts global action. */
    this.sendProgressiveBackInStockAlerts = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "sendProgressiveBackInStockAlerts",
      operationName: "sendProgressiveBackInStockAlerts",
      operationReturnType: "SendProgressiveBackInStockAlerts",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        productVariantId: { required: false, type: "String" }
      }
    });
    /** Executes the sendTemplateQueued global action. */
    this.sendTemplateQueued = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "sendTemplateQueued",
      operationName: "sendTemplateQueued",
      operationReturnType: "SendTemplateQueued",
      namespace: null,
      variables: {}
    });
    /** Executes the sendWhatsAppCampaign global action. */
    this.sendWhatsAppCampaign = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "sendWhatsAppCampaign",
      operationName: "sendWhatsAppCampaign",
      operationReturnType: "SendWhatsAppCampaign",
      namespace: null,
      variables: {
        phoneNumbers: { required: false, type: "[String!]" },
        campaignId: { required: false, type: "String" },
        sendingId: { required: false, type: "String" },
        sendingDate: { required: false, type: "String" },
        sendingBatchId: { required: false, type: "Float" },
        customerId: { required: false, type: "String" },
        productVariantId: { required: false, type: "String" },
        shopifyOrderId: { required: false, type: "String" },
        shopifyFulfillmentId: { required: false, type: "String" },
        isForced: { required: false, type: "Boolean" },
        abandonedCheckoutUrl: { required: false, type: "String" },
        imageUrl: { required: false, type: "String" },
        videoUrl: { required: false, type: "String" },
        url: { required: false, type: "String" },
        url2: { required: false, type: "String" },
        isTest: { required: false, type: "Boolean" },
        isFromApi: { required: false, type: "Boolean" },
        parameters: { required: false, type: "JSONObject" }
      }
    });
    /** Executes the sendWhatsAppMessage global action. */
    this.sendWhatsAppMessage = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "sendWhatsAppMessage",
      operationName: "sendWhatsAppMessage",
      operationReturnType: "SendWhatsAppMessage",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        contactNumber: { required: false, type: "String" },
        messageSkeletonId: { required: false, type: "String" },
        scenarioId: { required: false, type: "String" },
        parameters: { required: false, type: "SendWhatsAppMessageParametersInput" }
      }
    });
    /** Executes the sendWhatsAppTemplate global action. */
    this.sendWhatsAppTemplate = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "sendWhatsAppTemplate",
      operationName: "sendWhatsAppTemplate",
      operationReturnType: "SendWhatsAppTemplate",
      namespace: null,
      variables: {
        phoneNumber: { required: false, type: "String" },
        whatsappTemplateId: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        productVariantId: { required: false, type: "String" },
        shopifyOrderId: { required: false, type: "String" },
        shopifyFulfillmentId: { required: false, type: "String" },
        isForced: { required: false, type: "Boolean" },
        abandonedCheckoutUrl: { required: false, type: "String" }
      }
    });
    /** Executes the sendWhatsAppTextMessage global action. */
    this.sendWhatsAppTextMessage = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "sendWhatsAppTextMessage",
      operationName: "sendWhatsAppTextMessage",
      operationReturnType: "SendWhatsAppTextMessage",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        contactNumber: { required: false, type: "String" },
        body: { required: false, type: "String" },
        isAiGenerated: { required: false, type: "Boolean" }
      }
    });
    /** Executes the sendWhatsappFile global action. */
    this.sendWhatsappFile = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "sendWhatsappFile",
      operationName: "sendWhatsappFile",
      operationReturnType: "SendWhatsappFile",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        contactNumber: { required: false, type: "String" },
        fileType: { required: false, type: "String" },
        fileUrl: { required: false, type: "String" },
        fileName: { required: false, type: "String" }
      }
    });
    /** Executes the sendingAbandonedCartAlert global action. */
    this.sendingAbandonedCartAlert = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "sendingAbandonedCartAlert",
      operationName: "sendingAbandonedCartAlert",
      operationReturnType: "SendingAbandonedCartAlert",
      namespace: null,
      variables: {}
    });
    /** Executes the setCurrentSession global action. */
    this.setCurrentSession = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "setCurrentSession",
      operationName: "setCurrentSession",
      operationReturnType: "SetCurrentSession",
      namespace: null,
      variables: {
        userId: { required: false, type: "String" },
        sessionId: { required: false, type: "String" }
      }
    });
    /** Executes the setOptinStatusToShopifyAndKlaviyo global action. */
    this.setOptinStatusToShopifyAndKlaviyo = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "setOptinStatusToShopifyAndKlaviyo",
      operationName: "setOptinStatusToShopifyAndKlaviyo",
      operationReturnType: "SetOptinStatusToShopifyAndKlaviyo",
      namespace: null,
      variables: {}
    });
    /** Executes the setShopifyShopForCustomer global action. */
    this.setShopifyShopForCustomer = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "setShopifyShopForCustomer",
      operationName: "setShopifyShopForCustomer",
      operationReturnType: "SetShopifyShopForCustomer",
      namespace: null,
      variables: {}
    });
    /** Executes the shopifySyncAction global action. */
    this.shopifySyncAction = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "shopifySyncAction",
      operationName: "shopifySyncAction",
      operationReturnType: "ShopifySyncAction",
      namespace: null,
      variables: { shopId: { required: false, type: "String" } }
    });
    /** Executes the signInWithAccessToken global action. */
    this.signInWithAccessToken = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "signInWithAccessToken",
      operationName: "signInWithAccessToken",
      operationReturnType: "SignInWithAccessToken",
      namespace: null,
      variables: {
        accessToken: { required: false, type: "String" },
        destinationPath: { required: false, type: "String" }
      }
    });
    /** Executes the stopProcessingCampaign global action. */
    this.stopProcessingCampaign = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "stopProcessingCampaign",
      operationName: "stopProcessingCampaign",
      operationReturnType: "StopProcessingCampaign",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        campaignId: { required: false, type: "String" },
        sendingBatchId: { required: false, type: "Float" }
      }
    });
    /** Executes the sumbitTemplateTo360Dialog global action. */
    this.sumbitTemplateTo360Dialog = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "sumbitTemplateTo360Dialog",
      operationName: "sumbitTemplateTo360Dialog",
      operationReturnType: "SumbitTemplateTo360Dialog",
      namespace: null,
      variables: {
        template: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        buttonUrlLink: { required: false, type: "String" },
        buttonUrlLink2: { required: false, type: "String" },
        shouldUseRedirection: { required: false, type: "Boolean" },
        shouldUseRedirection2: { required: false, type: "Boolean" },
        trackingEnabled: { required: false, type: "Boolean" },
        trackingEnabled2: { required: false, type: "Boolean" },
        officialUrlButtons: { required: false, type: "String" }
      }
    });
    /** Executes the switchCustomer global action. */
    this.switchCustomer = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "switchCustomer",
      operationName: "switchCustomer",
      operationReturnType: "SwitchCustomer",
      namespace: null,
      variables: { customerId: { required: false, type: "String" } }
    });
    /** Executes the syncAutomationsCountToGoogleSheet global action. */
    this.syncAutomationsCountToGoogleSheet = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "syncAutomationsCountToGoogleSheet",
      operationName: "syncAutomationsCountToGoogleSheet",
      operationReturnType: "SyncAutomationsCountToGoogleSheet",
      namespace: null,
      variables: {}
    });
    /** Executes the syncCustomerStatsToGoogleSheet global action. */
    this.syncCustomerStatsToGoogleSheet = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "syncCustomerStatsToGoogleSheet",
      operationName: "syncCustomerStatsToGoogleSheet",
      operationReturnType: "SyncCustomerStatsToGoogleSheet",
      namespace: null,
      variables: {}
    });
    /** Executes the syncCustomersToGoogleSheet global action. */
    this.syncCustomersToGoogleSheet = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "syncCustomersToGoogleSheet",
      operationName: "syncCustomersToGoogleSheet",
      operationReturnType: "SyncCustomersToGoogleSheet",
      namespace: null,
      variables: {}
    });
    /** Executes the syncKlaviyoProfilesForOneCustomer global action. */
    this.syncKlaviyoProfilesForOneCustomer = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "syncKlaviyoProfilesForOneCustomer",
      operationName: "syncKlaviyoProfilesForOneCustomer",
      operationReturnType: "SyncKlaviyoProfilesForOneCustomer",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        dateFilter: { required: false, type: "String" },
        klaviyoOptinSegmentId: { required: false, type: "String" }
      }
    });
    /** Executes the syncKlaviyoProfilesForOnePage global action. */
    this.syncKlaviyoProfilesForOnePage = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "syncKlaviyoProfilesForOnePage",
      operationName: "syncKlaviyoProfilesForOnePage",
      operationReturnType: "SyncKlaviyoProfilesForOnePage",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        url: { required: false, type: "String" }
      }
    });
    /** Executes the syncMonthlyCustomerStatsToGoogleSheet global action. */
    this.syncMonthlyCustomerStatsToGoogleSheet = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "syncMonthlyCustomerStatsToGoogleSheet",
      operationName: "syncMonthlyCustomerStatsToGoogleSheet",
      operationReturnType: "SyncMonthlyCustomerStatsToGoogleSheet",
      namespace: null,
      variables: {
        month: { required: false, type: "String" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the syncOptinStatusWithJacadi global action. */
    this.syncOptinStatusWithJacadi = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "syncOptinStatusWithJacadi",
      operationName: "syncOptinStatusWithJacadi",
      operationReturnType: "SyncOptinStatusWithJacadi",
      namespace: null,
      variables: {
        contactId: { required: false, type: "String" },
        jacadiCustomerId: { required: false, type: "String" },
        phoneNumber: { required: false, type: "String" },
        isOptin: { required: false, type: "Boolean" },
        optinOrigin: { required: false, type: "String" }
      }
    });
    /** Executes the syncShopifyCheckout global action. */
    this.syncShopifyCheckout = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "syncShopifyCheckout",
      operationName: "syncShopifyCheckout",
      operationReturnType: "SyncShopifyCheckout",
      namespace: null,
      variables: {}
    });
    /** Executes the syncShopifyCustomer global action. */
    this.syncShopifyCustomer = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "syncShopifyCustomer",
      operationName: "syncShopifyCustomer",
      operationReturnType: "SyncShopifyCustomer",
      namespace: null,
      variables: { shopId: { required: false, type: "String" } }
    });
    /** Executes the tagContactsBatch global action. */
    this.tagContactsBatch = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "tagContactsBatch",
      operationName: "tagContactsBatch",
      operationReturnType: "TagContactsBatch",
      namespace: null,
      variables: { contactTagRecords: { required: false, type: "[JSONObject!]" } }
    });
    /** Executes the tagFilteredContacts global action. */
    this.tagFilteredContacts = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "tagFilteredContacts",
      operationName: "tagFilteredContacts",
      operationReturnType: "TagFilteredContacts",
      namespace: null,
      variables: {
        tagIds: { required: false, type: "[String!]" },
        apiFilters: { required: false, type: "[JSONObject!]" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the tempAddTagToShopify global action. */
    this.tempAddTagToShopify = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "tempAddTagToShopify",
      operationName: "tempAddTagToShopify",
      operationReturnType: "TempAddTagToShopify",
      namespace: null,
      variables: {}
    });
    /** Executes the tempAttributeMessageCustomer global action. */
    this.tempAttributeMessageCustomer = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "tempAttributeMessageCustomer",
      operationName: "tempAttributeMessageCustomer",
      operationReturnType: "TempAttributeMessageCustomer",
      namespace: null,
      variables: {}
    });
    /** Executes the tempSyncOldJacadiOptins global action. */
    this.tempSyncOldJacadiOptins = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "tempSyncOldJacadiOptins",
      operationName: "tempSyncOldJacadiOptins",
      operationReturnType: "TempSyncOldJacadiOptins",
      namespace: null,
      variables: {
        contactId: { required: false, type: "String" },
        optinAt: { required: false, type: "String" },
        jacadiCustomerId: { required: false, type: "String" },
        phoneNumber: { required: false, type: "String" },
        isOptin: { required: false, type: "Boolean" },
        optinOrigin: { required: false, type: "String" }
      }
    });
    /** Executes the testActionRules global action. */
    this.testActionRules = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "testActionRules",
      operationName: "testActionRules",
      operationReturnType: "TestActionRules",
      namespace: null,
      variables: {}
    });
    /** Executes the testAdobeOccitane global action. */
    this.testAdobeOccitane = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "testAdobeOccitane",
      operationName: "testAdobeOccitane",
      operationReturnType: "TestAdobeOccitane",
      namespace: null,
      variables: {}
    });
    /** Executes the testBugLoupiote global action. */
    this.testBugLoupiote = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "testBugLoupiote",
      operationName: "testBugLoupiote",
      operationReturnType: "TestBugLoupiote",
      namespace: null,
      variables: {}
    });
    /** Executes the testCloudinary global action. */
    this.testCloudinary = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "testCloudinary",
      operationName: "testCloudinary",
      operationReturnType: "TestCloudinary",
      namespace: null,
      variables: {}
    });
    /** Executes the testContactFetching global action. */
    this.testContactFetching = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "testContactFetching",
      operationName: "testContactFetching",
      operationReturnType: "TestContactFetching",
      namespace: null,
      variables: {}
    });
    /** Executes the testEmail global action. */
    this.testEmail = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "testEmail",
      operationName: "testEmail",
      operationReturnType: "TestEmail",
      namespace: null,
      variables: {}
    });
    /** Executes the testGPT global action. */
    this.testGPT = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "testGPT",
      operationName: "testGPT",
      operationReturnType: "TestGPT",
      namespace: null,
      variables: {
        messages: { required: false, type: "[TestGPTMessagesElementTypeInput!]" }
      }
    });
    /** Executes the testJacadiVoucherExpirationAutomation global action. */
    this.testJacadiVoucherExpirationAutomation = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "testJacadiVoucherExpirationAutomation",
      operationName: "testJacadiVoucherExpirationAutomation",
      operationReturnType: "TestJacadiVoucherExpirationAutomation",
      namespace: null,
      variables: { testPhoneNumbers: { required: false, type: "[String!]" } }
    });
    /** Executes the testSendDataToGoogleSheets global action. */
    this.testSendDataToGoogleSheets = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "testSendDataToGoogleSheets",
      operationName: "testSendDataToGoogleSheets",
      operationReturnType: "TestSendDataToGoogleSheets",
      namespace: null,
      variables: {}
    });
    /** Executes the testWhatsAppOrder global action. */
    this.testWhatsAppOrder = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "testWhatsAppOrder",
      operationName: "testWhatsAppOrder",
      operationReturnType: "TestWhatsAppOrder",
      namespace: null,
      variables: {}
    });
    /** Executes the toggleContactOptin global action. */
    this.toggleContactOptin = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "toggleContactOptin",
      operationName: "toggleContactOptin",
      operationReturnType: "ToggleContactOptin",
      namespace: null,
      variables: {
        shopId: { required: false, type: "String" },
        contactNumber: { required: false, type: "String" },
        hasOptIn: { required: false, type: "Boolean" }
      }
    });
    /** Executes the triggerSamsungBirthdayCampaign global action. */
    this.triggerSamsungBirthdayCampaign = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "triggerSamsungBirthdayCampaign",
      operationName: "triggerSamsungBirthdayCampaign",
      operationReturnType: "TriggerSamsungBirthdayCampaign",
      namespace: null,
      variables: {}
    });
    /** Executes the triggerSamsungDedupRoutine global action. */
    this.triggerSamsungDedupRoutine = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "triggerSamsungDedupRoutine",
      operationName: "triggerSamsungDedupRoutine",
      operationReturnType: "TriggerSamsungDedupRoutine",
      namespace: null,
      variables: {}
    });
    /** Executes the triggerSamsungImportRoutine global action. */
    this.triggerSamsungImportRoutine = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "triggerSamsungImportRoutine",
      operationName: "triggerSamsungImportRoutine",
      operationReturnType: "TriggerSamsungImportRoutine",
      namespace: null,
      variables: {}
    });
    /** Executes the triggerSamsungOrderTrackingAutomation global action. */
    this.triggerSamsungOrderTrackingAutomation = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "triggerSamsungOrderTrackingAutomation",
      operationName: "triggerSamsungOrderTrackingAutomation",
      operationReturnType: "TriggerSamsungOrderTrackingAutomation",
      namespace: null,
      variables: {}
    });
    /** Executes the triggerSamsungShippingTrackingAutomation global action. */
    this.triggerSamsungShippingTrackingAutomation = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "triggerSamsungShippingTrackingAutomation",
      operationName: "triggerSamsungShippingTrackingAutomation",
      operationReturnType: "TriggerSamsungShippingTrackingAutomation",
      namespace: null,
      variables: {}
    });
    /** Executes the triggerSamsungShopWelcomeFlow global action. */
    this.triggerSamsungShopWelcomeFlow = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "triggerSamsungShopWelcomeFlow",
      operationName: "triggerSamsungShopWelcomeFlow",
      operationReturnType: "TriggerSamsungShopWelcomeFlow",
      namespace: null,
      variables: {}
    });
    /** Executes the triggerTurnoverFetchingFromSplio global action. */
    this.triggerTurnoverFetchingFromSplio = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "triggerTurnoverFetchingFromSplio",
      operationName: "triggerTurnoverFetchingFromSplio",
      operationReturnType: "TriggerTurnoverFetchingFromSplio",
      namespace: null,
      variables: {}
    });
    /** Executes the triggerWhatsAppRules global action. */
    this.triggerWhatsAppRules = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "triggerWhatsAppRules",
      operationName: "triggerWhatsAppRules",
      operationReturnType: "TriggerWhatsAppRules",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        contactNumber: { required: false, type: "String" },
        eventType: { required: false, type: "String" },
        body: { required: false, type: "String" },
        buttonReplyId: { required: false, type: "String" }
      }
    });
    /** Executes the unblockUser global action. */
    this.unblockUser = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "unblockUser",
      operationName: "unblockUser",
      operationReturnType: "UnblockUser",
      namespace: null,
      variables: { email: { required: false, type: "String" } }
    });
    /** Executes the updateAirFranceGlobalVariables global action. */
    this.updateAirFranceGlobalVariables = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "updateAirFranceGlobalVariables",
      operationName: "updateAirFranceGlobalVariables",
      operationReturnType: "UpdateAirFranceGlobalVariables",
      namespace: null,
      variables: {}
    });
    /** Executes the updateAttributedUser global action. */
    this.updateAttributedUser = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "updateAttributedUser",
      operationName: "updateAttributedUser",
      operationReturnType: "UpdateAttributedUser",
      namespace: null,
      variables: {}
    });
    /** Executes the updateBisAttribution global action. */
    this.updateBisAttribution = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "updateBisAttribution",
      operationName: "updateBisAttribution",
      operationReturnType: "UpdateBisAttribution",
      namespace: null,
      variables: {}
    });
    /** Executes the updateCampaignAttribution global action. */
    this.updateCampaignAttribution = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "updateCampaignAttribution",
      operationName: "updateCampaignAttribution",
      operationReturnType: "UpdateCampaignAttribution",
      namespace: null,
      variables: {}
    });
    /** Executes the updateContactBatch global action. */
    this.updateContactBatch = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "updateContactBatch",
      operationName: "updateContactBatch",
      operationReturnType: "UpdateContactBatch",
      namespace: null,
      variables: {
        tagId: { required: false, type: "String" },
        shouldSkipOnSuccess: { required: false, type: "Boolean" },
        contacts: { required: false, type: "[JSONObject!]" }
      }
    });
    /** Executes the updateContactBatchWithMultipleTags global action. */
    this.updateContactBatchWithMultipleTags = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "updateContactBatchWithMultipleTags",
      operationName: "updateContactBatchWithMultipleTags",
      operationReturnType: "UpdateContactBatchWithMultipleTags",
      namespace: null,
      variables: {
        tagIds: { required: false, type: "[String!]" },
        csvImportId: { required: false, type: "String" },
        shouldSkipOnSuccess: { required: false, type: "Boolean" },
        contacts: { required: false, type: "[JSONObject!]" }
      }
    });
    /** Executes the updateContactOptinDates global action. */
    this.updateContactOptinDates = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "updateContactOptinDates",
      operationName: "updateContactOptinDates",
      operationReturnType: "UpdateContactOptinDates",
      namespace: null,
      variables: {}
    });
    /** Executes the updateJacadiJAC5MembershipStatus global action. */
    this.updateJacadiJAC5MembershipStatus = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "updateJacadiJAC5MembershipStatus",
      operationName: "updateJacadiJAC5MembershipStatus",
      operationReturnType: "UpdateJacadiJAC5MembershipStatus",
      namespace: null,
      variables: {}
    });
    /** Executes the updateLastTemplateSent global action. */
    this.updateLastTemplateSent = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "updateLastTemplateSent",
      operationName: "updateLastTemplateSent",
      operationReturnType: "UpdateLastTemplateSent",
      namespace: null,
      variables: {}
    });
    /** Executes the updateLibraryFlowFromGoogleSheet global action. */
    this.updateLibraryFlowFromGoogleSheet = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "updateLibraryFlowFromGoogleSheet",
      operationName: "updateLibraryFlowFromGoogleSheet",
      operationReturnType: "UpdateLibraryFlowFromGoogleSheet",
      namespace: null,
      variables: {}
    });
    /** Executes the updateMessageStatus global action. */
    this.updateMessageStatus = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "updateMessageStatus",
      operationName: "updateMessageStatus",
      operationReturnType: "UpdateMessageStatus",
      namespace: null,
      variables: {
        status: { required: false, type: "String" },
        errors: { required: false, type: "String" },
        whatsappMessageId: { required: false, type: "String" },
        pricing: { required: false, type: "String" },
        recipient_id: { required: false, type: "String" },
        conversation: { required: false, type: "String" },
        timestamp: { required: false, type: "String" }
      }
    });
    /** Executes the updateMockedCampaigns global action. */
    this.updateMockedCampaigns = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "updateMockedCampaigns",
      operationName: "updateMockedCampaigns",
      operationReturnType: "UpdateMockedCampaigns",
      namespace: null,
      variables: {}
    });
    /** Executes the updateOrderAttribution global action. */
    this.updateOrderAttribution = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "updateOrderAttribution",
      operationName: "updateOrderAttribution",
      operationReturnType: "UpdateOrderAttribution",
      namespace: null,
      variables: {
        campaignId: { required: false, type: "String" },
        shopifyOrderId: { required: false, type: "String" }
      }
    });
    /** Executes the updateParrainContact global action. */
    this.updateParrainContact = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "updateParrainContact",
      operationName: "updateParrainContact",
      operationReturnType: "UpdateParrainContact",
      namespace: null,
      variables: {
        parrainPhoneNumber: { required: false, type: "String" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the updateSamsungContactsBatch global action. */
    this.updateSamsungContactsBatch = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "updateSamsungContactsBatch",
      operationName: "updateSamsungContactsBatch",
      operationReturnType: "UpdateSamsungContactsBatch",
      namespace: null,
      variables: {
        traceId: { required: false, type: "String" },
        contacts: { required: false, type: "String" },
        batchIndex: { required: false, type: "Float" },
        totalBatches: { required: false, type: "Float" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the updateScheduledCampaigns global action. */
    this.updateScheduledCampaigns = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "updateScheduledCampaigns",
      operationName: "updateScheduledCampaigns",
      operationReturnType: "UpdateScheduledCampaigns",
      namespace: null,
      variables: {}
    });
    /** Executes the updateSegmentMembershipBySegment global action. */
    this.updateSegmentMembershipBySegment = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "updateSegmentMembershipBySegment",
      operationName: "updateSegmentMembershipBySegment",
      operationReturnType: "UpdateSegmentMembershipBySegment",
      namespace: null,
      variables: {
        segmentId: { required: false, type: "String" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the updateShopifyPlanForCustomer global action. */
    this.updateShopifyPlanForCustomer = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "updateShopifyPlanForCustomer",
      operationName: "updateShopifyPlanForCustomer",
      operationReturnType: "UpdateShopifyPlanForCustomer",
      namespace: null,
      variables: {}
    });
    /** Executes the updateShopifyPlans global action. */
    this.updateShopifyPlans = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "updateShopifyPlans",
      operationName: "updateShopifyPlans",
      operationReturnType: "UpdateShopifyPlans",
      namespace: null,
      variables: {}
    });
    /** Executes the updateTemplateHandle global action. */
    this.updateTemplateHandle = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "updateTemplateHandle",
      operationName: "updateTemplateHandle",
      operationReturnType: "UpdateTemplateHandle",
      namespace: null,
      variables: {}
    });
    /** Executes the updateUsersNames global action. */
    this.updateUsersNames = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "updateUsersNames",
      operationName: "updateUsersNames",
      operationReturnType: "UpdateUsersNames",
      namespace: null,
      variables: {
        userData: {
          required: false,
          type: "[UpdateUsersNamesUserDataElementTypeInput!]"
        }
      }
    });
    /** Executes the validateShopifyOrdersAttribution global action. */
    this.validateShopifyOrdersAttribution = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "validateShopifyOrdersAttribution",
      operationName: "validateShopifyOrdersAttribution",
      operationReturnType: "ValidateShopifyOrdersAttribution",
      namespace: null,
      variables: { startDate: { required: false, type: "String" } }
    });
    /** Executes the waitForADuration global action. */
    this.waitForADuration = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "waitForADuration",
      operationName: "waitForADuration",
      operationReturnType: "WaitForADuration",
      namespace: null,
      variables: {
        nodeId: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        contactNumber: { required: false, type: "String" },
        abandonedCheckoutUrl: { required: false, type: "String" }
      }
    });
    /** Executes the waitForWhatsappMessage global action. */
    this.waitForWhatsappMessage = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "waitForWhatsappMessage",
      operationName: "waitForWhatsappMessage",
      operationReturnType: "WaitForWhatsappMessage",
      namespace: null,
      variables: {
        nodeId: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        contactNumber: { required: false, type: "String" }
      }
    });
    /** Executes the waitForWhatsappMessageStatus global action. */
    this.waitForWhatsappMessageStatus = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "waitForWhatsappMessageStatus",
      operationName: "waitForWhatsappMessageStatus",
      operationReturnType: "WaitForWhatsappMessageStatus",
      namespace: null,
      variables: {
        nodeId: { required: false, type: "String" },
        customerId: { required: false, type: "String" },
        contactNumber: { required: false, type: "String" }
      }
    });
    /** Executes the writeCampaignsDataToGoogleSheet global action. */
    this.writeCampaignsDataToGoogleSheet = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "writeCampaignsDataToGoogleSheet",
      operationName: "writeCampaignsDataToGoogleSheet",
      operationReturnType: "WriteCampaignsDataToGoogleSheet",
      namespace: null,
      variables: {
        customerId: { required: false, type: "String" },
        dataExport: { required: false, type: "String" },
        sheetId: { required: false, type: "String" },
        isShopifyCustomer: { required: false, type: "Boolean" }
      }
    });
    /** Executes the getAnalyticsDelivered computed view. */
    this.getAnalyticsDelivered = buildComputedView(this, {
      type: "computedView",
      operationName: "getAnalyticsDelivered",
      functionName: "getAnalyticsDelivered",
      gqlFieldName: "getAnalyticsDelivered",
      referencedModelKeys: ["DataModel-a3NoPGYITtkC"],
      referencedTypenames: ["Message"],
      namespace: null,
      variables: {
        from: { required: false, type: "DateTime" },
        to: { required: false, type: "DateTime" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the getAnalyticsDeliveredByCampaignId computed view. */
    this.getAnalyticsDeliveredByCampaignId = buildComputedView(this, {
      type: "computedView",
      operationName: "getAnalyticsDeliveredByCampaignId",
      functionName: "getAnalyticsDeliveredByCampaignId",
      gqlFieldName: "getAnalyticsDeliveredByCampaignId",
      referencedModelKeys: ["DataModel-a3NoPGYITtkC"],
      referencedTypenames: ["Message"],
      namespace: null,
      variables: {
        from: { required: false, type: "DateTime" },
        to: { required: false, type: "DateTime" },
        customerId: { required: false, type: "String" },
        campaignId: { required: false, type: "String" }
      }
    });
    /** Executes the getAnalyticsExternalTurnover computed view. */
    this.getAnalyticsExternalTurnover = buildComputedView(this, {
      type: "computedView",
      operationName: "getAnalyticsExternalTurnover",
      functionName: "getAnalyticsExternalTurnover",
      gqlFieldName: "getAnalyticsExternalTurnover",
      referencedModelKeys: ["DataModel-a3NoPGYITtkC"],
      referencedTypenames: ["Message"],
      namespace: null,
      variables: {
        from: { required: false, type: "DateTime" },
        to: { required: false, type: "DateTime" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the getAnalyticsExternalTurnoverByCampaignId computed view. */
    this.getAnalyticsExternalTurnoverByCampaignId = buildComputedView(this, {
      type: "computedView",
      operationName: "getAnalyticsExternalTurnoverByCampaignId",
      functionName: "getAnalyticsExternalTurnoverByCampaignId",
      gqlFieldName: "getAnalyticsExternalTurnoverByCampaignId",
      referencedModelKeys: ["DataModel-a3NoPGYITtkC"],
      referencedTypenames: ["Message"],
      namespace: null,
      variables: {
        from: { required: false, type: "DateTime" },
        to: { required: false, type: "DateTime" },
        customerId: { required: false, type: "String" },
        campaignId: { required: false, type: "String" }
      }
    });
    /** Executes the getAnalyticsMessagesCount computed view. */
    this.getAnalyticsMessagesCount = buildComputedView(this, {
      type: "computedView",
      operationName: "getAnalyticsMessagesCount",
      functionName: "getAnalyticsMessagesCount",
      gqlFieldName: "getAnalyticsMessagesCount",
      referencedModelKeys: ["DataModel-a3NoPGYITtkC"],
      referencedTypenames: ["Message"],
      namespace: null,
      variables: {
        from: { required: false, type: "DateTime" },
        to: { required: false, type: "DateTime" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the getAnalyticsMessagesCountByCampaignId computed view. */
    this.getAnalyticsMessagesCountByCampaignId = buildComputedView(this, {
      type: "computedView",
      operationName: "getAnalyticsMessagesCountByCampaignId",
      functionName: "getAnalyticsMessagesCountByCampaignId",
      gqlFieldName: "getAnalyticsMessagesCountByCampaignId",
      referencedModelKeys: ["DataModel-a3NoPGYITtkC"],
      referencedTypenames: ["Message"],
      namespace: null,
      variables: {
        from: { required: false, type: "DateTime" },
        to: { required: false, type: "DateTime" },
        customerId: { required: false, type: "String" },
        campaignId: { required: false, type: "String" }
      }
    });
    /** Executes the getAnalyticsOptins computed view. */
    this.getAnalyticsOptins = buildComputedView(this, {
      type: "computedView",
      operationName: "getAnalyticsOptins",
      functionName: "getAnalyticsOptins",
      gqlFieldName: "getAnalyticsOptins",
      referencedModelKeys: ["DataModel-o8cPMtyFVx_K"],
      referencedTypenames: ["Contact"],
      namespace: null,
      variables: {
        from: { required: false, type: "DateTime" },
        to: { required: false, type: "DateTime" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the getAnalyticsOptouts computed view. */
    this.getAnalyticsOptouts = buildComputedView(this, {
      type: "computedView",
      operationName: "getAnalyticsOptouts",
      functionName: "getAnalyticsOptouts",
      gqlFieldName: "getAnalyticsOptouts",
      referencedModelKeys: ["DataModel-o8cPMtyFVx_K"],
      referencedTypenames: ["Contact"],
      namespace: null,
      variables: {
        from: { required: false, type: "DateTime" },
        to: { required: false, type: "DateTime" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the getAnalyticsOptoutsByCampaignId computed view. */
    this.getAnalyticsOptoutsByCampaignId = buildComputedView(this, {
      type: "computedView",
      operationName: "getAnalyticsOptoutsByCampaignId",
      functionName: "getAnalyticsOptoutsByCampaignId",
      gqlFieldName: "getAnalyticsOptoutsByCampaignId",
      referencedModelKeys: [
        "DataModel-o8cPMtyFVx_K",
        "DataModel-a3NoPGYITtkC",
        "DataModel-aU1wm7GuUtMt"
      ],
      referencedTypenames: ["Contact", "Message", "Scenario"],
      namespace: null,
      variables: {
        from: { required: false, type: "DateTime" },
        to: { required: false, type: "DateTime" },
        customerId: { required: false, type: "String" },
        campaignId: { required: false, type: "String" }
      }
    });
    /** Executes the getAnalyticsRead computed view. */
    this.getAnalyticsRead = buildComputedView(this, {
      type: "computedView",
      operationName: "getAnalyticsRead",
      functionName: "getAnalyticsRead",
      gqlFieldName: "getAnalyticsRead",
      referencedModelKeys: ["DataModel-a3NoPGYITtkC"],
      referencedTypenames: ["Message"],
      namespace: null,
      variables: {
        from: { required: false, type: "DateTime" },
        to: { required: false, type: "DateTime" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the getAnalyticsReadByCampaignId computed view. */
    this.getAnalyticsReadByCampaignId = buildComputedView(this, {
      type: "computedView",
      operationName: "getAnalyticsReadByCampaignId",
      functionName: "getAnalyticsReadByCampaignId",
      gqlFieldName: "getAnalyticsReadByCampaignId",
      referencedModelKeys: ["DataModel-a3NoPGYITtkC"],
      referencedTypenames: ["Message"],
      namespace: null,
      variables: {
        from: { required: false, type: "DateTime" },
        to: { required: false, type: "DateTime" },
        customerId: { required: false, type: "String" },
        campaignId: { required: false, type: "String" }
      }
    });
    /** Executes the getAnalyticsSales computed view. */
    this.getAnalyticsSales = buildComputedView(this, {
      type: "computedView",
      operationName: "getAnalyticsSales",
      functionName: "getAnalyticsSales",
      gqlFieldName: "getAnalyticsSales",
      referencedModelKeys: ["DataModel-Shopify-Order"],
      referencedTypenames: ["ShopifyOrder"],
      namespace: null,
      variables: {
        from: { required: false, type: "DateTime" },
        to: { required: false, type: "DateTime" },
        shopId: { required: false, type: "String" }
      }
    });
    /** Executes the getAnalyticsSalesByCampaignId computed view. */
    this.getAnalyticsSalesByCampaignId = buildComputedView(this, {
      type: "computedView",
      operationName: "getAnalyticsSalesByCampaignId",
      functionName: "getAnalyticsSalesByCampaignId",
      gqlFieldName: "getAnalyticsSalesByCampaignId",
      referencedModelKeys: ["DataModel-Shopify-Order"],
      referencedTypenames: ["ShopifyOrder"],
      namespace: null,
      variables: {
        from: { required: false, type: "DateTime" },
        to: { required: false, type: "DateTime" },
        shopId: { required: false, type: "String" },
        campaignId: { required: false, type: "String" }
      }
    });
    /** Executes the getAnalyticsSent computed view. */
    this.getAnalyticsSent = buildComputedView(this, {
      type: "computedView",
      operationName: "getAnalyticsSent",
      functionName: "getAnalyticsSent",
      gqlFieldName: "getAnalyticsSent",
      referencedModelKeys: ["DataModel-a3NoPGYITtkC"],
      referencedTypenames: ["Message"],
      namespace: null,
      variables: {
        from: { required: false, type: "DateTime" },
        to: { required: false, type: "DateTime" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the getAnalyticsSentByCampaignId computed view. */
    this.getAnalyticsSentByCampaignId = buildComputedView(this, {
      type: "computedView",
      operationName: "getAnalyticsSentByCampaignId",
      functionName: "getAnalyticsSentByCampaignId",
      gqlFieldName: "getAnalyticsSentByCampaignId",
      referencedModelKeys: ["DataModel-a3NoPGYITtkC"],
      referencedTypenames: ["Message"],
      namespace: null,
      variables: {
        from: { required: false, type: "DateTime" },
        to: { required: false, type: "DateTime" },
        customerId: { required: false, type: "String" },
        campaignId: { required: false, type: "String" }
      }
    });
    /** Executes the getAnalyticsTurnover computed view. */
    this.getAnalyticsTurnover = buildComputedView(this, {
      type: "computedView",
      operationName: "getAnalyticsTurnover",
      functionName: "getAnalyticsTurnover",
      gqlFieldName: "getAnalyticsTurnover",
      referencedModelKeys: ["DataModel-Shopify-Order"],
      referencedTypenames: ["ShopifyOrder"],
      namespace: null,
      variables: {
        from: { required: false, type: "DateTime" },
        to: { required: false, type: "DateTime" },
        shopId: { required: false, type: "String" }
      }
    });
    /** Executes the getAnalyticsTurnoverByCampaignId computed view. */
    this.getAnalyticsTurnoverByCampaignId = buildComputedView(this, {
      type: "computedView",
      operationName: "getAnalyticsTurnoverByCampaignId",
      functionName: "getAnalyticsTurnoverByCampaignId",
      gqlFieldName: "getAnalyticsTurnoverByCampaignId",
      referencedModelKeys: ["DataModel-Shopify-Order"],
      referencedTypenames: ["ShopifyOrder"],
      namespace: null,
      variables: {
        from: { required: false, type: "DateTime" },
        to: { required: false, type: "DateTime" },
        shopId: { required: false, type: "String" },
        campaignId: { required: false, type: "String" }
      }
    });
    /** Executes the getAnalyticsWhatsappCost computed view. */
    this.getAnalyticsWhatsappCost = buildComputedView(this, {
      type: "computedView",
      operationName: "getAnalyticsWhatsappCost",
      functionName: "getAnalyticsWhatsappCost",
      gqlFieldName: "getAnalyticsWhatsappCost",
      referencedModelKeys: ["DataModel-a3NoPGYITtkC"],
      referencedTypenames: ["Message"],
      namespace: null,
      variables: {
        from: { required: false, type: "DateTime" },
        to: { required: false, type: "DateTime" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the getAnalyticsWhatsappCostByCampaignId computed view. */
    this.getAnalyticsWhatsappCostByCampaignId = buildComputedView(this, {
      type: "computedView",
      operationName: "getAnalyticsWhatsappCostByCampaignId",
      functionName: "getAnalyticsWhatsappCostByCampaignId",
      gqlFieldName: "getAnalyticsWhatsappCostByCampaignId",
      referencedModelKeys: ["DataModel-a3NoPGYITtkC"],
      referencedTypenames: ["Message"],
      namespace: null,
      variables: {
        from: { required: false, type: "DateTime" },
        to: { required: false, type: "DateTime" },
        customerId: { required: false, type: "String" },
        campaignId: { required: false, type: "String" }
      }
    });
    /** Executes the getCampaignIdsWithMetrics computed view. */
    this.getCampaignIdsWithMetrics = buildComputedView(this, {
      type: "computedView",
      operationName: "getCampaignIdsWithMetrics",
      functionName: "getCampaignIdsWithMetrics",
      gqlFieldName: "getCampaignIdsWithMetrics",
      referencedModelKeys: ["DataModel-a3NoPGYITtkC"],
      referencedTypenames: ["Message"],
      namespace: null,
      variables: {
        from: { required: false, type: "DateTime" },
        to: { required: false, type: "DateTime" },
        customerId: { required: false, type: "String" }
      }
    });
    /** Executes the getCsvImportProgress computed view. */
    this.getCsvImportProgress = buildComputedView(this, {
      type: "computedView",
      operationName: "getCsvImportProgress",
      functionName: "getCsvImportProgress",
      gqlFieldName: "getCsvImportProgress",
      referencedModelKeys: ["DataModel-zcTNqqaH2ey7"],
      referencedTypenames: ["ContactTag"],
      namespace: null,
      variables: {
        tagId: { required: false, type: "String" },
        fromDate: { required: false, type: "DateTime" }
      }
    });
    /** Executes the getCustomCouponCodeCategories computed view. */
    this.getCustomCouponCodeCategories = buildComputedView(this, {
      type: "computedView",
      operationName: "getCustomCouponCodeCategories",
      functionName: "getCustomCouponCodeCategories",
      gqlFieldName: "getCustomCouponCodeCategories",
      referencedModelKeys: ["2IKFDOkW3SVv"],
      referencedTypenames: ["CustomCouponCode"],
      namespace: null,
      variables: void 0
    });
    /** Executes the getCustomCouponCodeSummaryByCategory computed view. */
    this.getCustomCouponCodeSummaryByCategory = buildComputedView(this, {
      type: "computedView",
      operationName: "getCustomCouponCodeSummaryByCategory",
      functionName: "getCustomCouponCodeSummaryByCategory",
      gqlFieldName: "getCustomCouponCodeSummaryByCategory",
      referencedModelKeys: ["2IKFDOkW3SVv"],
      referencedTypenames: ["CustomCouponCode"],
      namespace: null,
      variables: { category: { required: false, type: "String" } }
    });
    /** Executes the getScheduledCampaign computed view. */
    this.getScheduledCampaign = buildComputedView(this, {
      type: "computedView",
      operationName: "getScheduledCampaign",
      functionName: "getScheduledCampaign",
      gqlFieldName: "getScheduledCampaign",
      referencedModelKeys: [
        "DataModel-qKYFKcvExAYo",
        "DataModel-7LxJ3n4W-ROt",
        "DataModel-o8cPMtyFVx_K",
        "DataModel-wL7uTI_iCKe7",
        "Jz9LHWucjERg"
      ],
      referencedTypenames: [
        "Sending",
        "Conversation",
        "Contact",
        "Customer",
        "SendingSummary"
      ],
      namespace: null,
      variables: {
        from: { required: false, type: "DateTime" },
        to: { required: false, type: "DateTime" }
      }
    });
    /** Executes the getSendingToPause computed view. */
    this.getSendingToPause = buildComputedView(this, {
      type: "computedView",
      operationName: "getSendingToPause",
      functionName: "getSendingToPause",
      gqlFieldName: "getSendingToPause",
      referencedModelKeys: [
        "DataModel-qKYFKcvExAYo",
        "DataModel-a3NoPGYITtkC",
        "DataModel-Shopify-Order",
        "DataModel-o8cPMtyFVx_K",
        "DataModel-ADaOI0Olsx-k"
      ],
      referencedTypenames: ["Sending", "Message", "ShopifyOrder", "Contact", "Campaign"],
      namespace: null,
      variables: {
        minDate: { required: false, type: "DateTime" },
        customerId: { required: false, type: "String" },
        campaignIds: { required: false, type: "[Float]" }
      }
    });
    /** Executes the getSentMessagesThisMonthByCustomerId computed view. */
    this.getSentMessagesThisMonthByCustomerId = buildComputedView(this, {
      type: "computedView",
      operationName: "getSentMessagesThisMonthByCustomerId",
      functionName: "getSentMessagesThisMonthByCustomerId",
      gqlFieldName: "getSentMessagesThisMonthByCustomerId",
      referencedModelKeys: ["DataModel-a3NoPGYITtkC"],
      referencedTypenames: ["Message"],
      namespace: null,
      variables: { customerId: { required: false, type: "String" } }
    });
    /** Executes the getTagsStats computed view. */
    this.getTagsStats = buildComputedView(this, {
      type: "computedView",
      operationName: "getTagsStats",
      functionName: "getTagsStats",
      gqlFieldName: "getTagsStats",
      referencedModelKeys: [
        "DataModel-zcTNqqaH2ey7",
        "DataModel-o8cPMtyFVx_K",
        "DataModel-jRr3bfh3Prx1"
      ],
      referencedTypenames: ["ContactTag", "Contact", "Tag"],
      namespace: null,
      variables: { customerId: { required: false, type: "String" } }
    });
    /** Executes an inline computed view. */
    this.view = buildInlineComputedView(this, {
      type: "computedView",
      operationName: "gellyView",
      functionName: "view",
      gqlFieldName: "gellyView",
      namespace: null,
      variables: {
        query: { type: "String", required: true },
        args: { type: "JSONObject" }
      }
    });
    /**
     * The list of environments with a customized API root endpoint
     */
    this.apiRoots = { "development": "https://getsimio--development.gadget.app/", "production": "https://app.getsimio.com/" };
    this.applicationId = "22129";
    /** Start a transaction against the Gadget backend which will atomically commit (or rollback). */
    this.transaction = async (callback) => {
      return await this.connection.transaction(callback);
    };
    /**
    * Get a new direct upload token for file uploads to directly to cloud storage.
    * See https://docs.gadget.dev/guides/storing-files#direct-uploads-using-tokens for more information
    * @return { url: string, token: string } A `url` to upload one file to, and a token for that file to pass back to Gadget as an action input.
    */
    this.getDirectUploadToken = async () => {
      const result = await this.query("query GetDirectUploadToken($nonce: String) { gadgetMeta { directUploadToken(nonce: $nonce) { url, token } } }", { nonce: Math.random().toString(36).slice(2, 7) }, {
        requestPolicy: "network-only"
      });
      return result.gadgetMeta.directUploadToken;
    };
    let inSSRContext = false;
    this.options = options;
    try {
      inSSRContext = !!(import.meta.env && import.meta.env.SSR);
    } catch (error) {
    }
    if (inSSRContext) {
      const api = globalThis.GadgetGlobals?.api;
      if (api) {
        return api.actAsSession;
      }
    }
    this.environment = (options?.environment ?? getImplicitEnv() ?? fallbackEnv).toLocaleLowerCase();
    let apiRoot;
    if (this.apiRoots[this.environment]) {
      apiRoot = this.apiRoots[this.environment];
    } else {
      const envPart = this.environment == productionEnv ? "" : `--${this.environment}`;
      apiRoot = `https://getsimio${envPart}.gadget.app`;
    }
    const exchanges = { ...options?.exchanges };
    if (this.environment !== productionEnv) {
      const devHarnessExchange = ({ forward }) => {
        return (operations$) => {
          const operationResult$ = forward(operations$);
          return pipe(
            operationResult$,
            map((result) => {
              try {
                if (typeof window !== "undefined" && typeof CustomEvent === "function") {
                  const event = new CustomEvent("gadget:devharness:graphqlresult", { detail: result });
                  window.dispatchEvent(event);
                }
              } catch (error) {
                console.warn("[gadget] error dispatching gadget dev harness event", error);
              }
              return result;
            })
          );
        };
      };
      exchanges.beforeAll = [
        devHarnessExchange,
        ...exchanges.beforeAll ?? []
      ];
    }
    const connectionOptions = {
      endpoint: new URL("api/graphql", apiRoot).toString(),
      applicationId: this.applicationId,
      authenticationMode: options?.authenticationMode,
      ...options,
      exchanges,
      environment: this.environment
    };
    const authenticationMode = maybeGetAuthenticationModeOptionsFromClientOptions(options ?? {});
    connectionOptions.authenticationMode = authenticationMode ?? (typeof window == "undefined" ? { anonymous: true } : { browserSession: true });
    this.connection = new GadgetConnection(connectionOptions);
    if (typeof window != "undefined" && typeof window.document != "undefined" && this.connection.authenticationMode == AuthenticationMode.APIKey && !options?.authenticationMode?.dangerouslyAllowBrowserApiKey) {
      throw new Error("GGT_BROWSER_API_KEY_USAGE: Using a Gadget API key to authenticate this client object is insecure and will leak your API keys to attackers. Please use a different authentication mode.");
    }
    if (typeof options?.authenticationMode === "undefined" && typeof window !== "undefined" && window.shopify?.idToken) {
      this.connection.setAuthenticationMode({
        custom: {
          async processFetch(_input, init) {
            const headers = new Headers(init.headers);
            const idToken = await window.shopify.idToken();
            headers.append("Authorization", "ShopifySessionToken " + idToken);
            init.headers ?? (init.headers = {});
            headers.forEach(function(value, key) {
              init.headers[key] = value;
            });
          },
          async processTransactionConnectionParams(params) {
            const idToken = await window.shopify.idToken();
            params.auth.shopifySessionToken = idToken;
          }
        }
      });
    }
    this.whatsappContact = new WhatsappContactManager(this.connection);
    this.campaignStats = new CampaignStatsManager(this.connection);
    this.customCouponCode = new CustomCouponCodeManager(this.connection);
    this.contactStats = new ContactStatsManager(this.connection);
    this.libraryFlow = new LibraryFlowManager(this.connection);
    this.whatsappTrigger = new WhatsappTriggerManager(this.connection);
    this.shopifyPlan = new ShopifyPlanManager(this.connection);
    this.replyButton = new ReplyButtonManager(this.connection);
    this.flowEdge = new FlowEdgeManager(this.connection);
    this.sendTemplateQueue = new SendTemplateQueueManager(this.connection);
    this.session = new SessionManager(this.connection);
    this.currentSession = new CurrentSessionManager(this.connection);
    this.abandonedCartAlert = new AbandonedCartAlertManager(this.connection);
    this.conversation = new ConversationManager(this.connection);
    this.campaignTemplate = new CampaignTemplateManager(this.connection);
    this.contactList = new ContactListManager(this.connection);
    this.campaign = new CampaignManager(this.connection);
    this.campaignDayStats = new CampaignDayStatsManager(this.connection);
    this.flowNode = new FlowNodeManager(this.connection);
    this.whatsappTemplate = new WhatsappTemplateManager(this.connection);
    this.shopifyAppInstallation = new ShopifyAppInstallationManager(this.connection);
    this.shopifyAppPurchaseOneTime = new ShopifyAppPurchaseOneTimeManager(this.connection);
    this.shopifyAppSubscription = new ShopifyAppSubscriptionManager(this.connection);
    this.shopifyCart = new ShopifyCartManager(this.connection);
    this.shopifyCartLineItem = new ShopifyCartLineItemManager(this.connection);
    this.shopifyCheckout = new ShopifyCheckoutManager(this.connection);
    this.shopifyCheckoutLineItem = new ShopifyCheckoutLineItemManager(this.connection);
    this.shopifyCustomer = new ShopifyCustomerManager(this.connection);
    this.shopifyFulfillment = new ShopifyFulfillmentManager(this.connection);
    this.shopifyFulfillmentEvent = new ShopifyFulfillmentEventManager(this.connection);
    this.shopifyFulfillmentLineItem = new ShopifyFulfillmentLineItemManager(this.connection);
    this.shopifyGdprRequest = new ShopifyGdprRequestManager(this.connection);
    this.shopifyOrder = new ShopifyOrderManager(this.connection);
    this.shopifyOrderLineItem = new ShopifyOrderLineItemManager(this.connection);
    this.shopifyProduct = new ShopifyProductManager(this.connection);
    this.shopifyProductImage = new ShopifyProductImageManager(this.connection);
    this.shopifyProductVariant = new ShopifyProductVariantManager(this.connection);
    this.shopifyShippingAddress = new ShopifyShippingAddressManager(this.connection);
    this.shopifyShop = new ShopifyShopManager(this.connection);
    this.shopifySync = new ShopifySyncManager(this.connection);
    this.message = new MessageManager(this.connection);
    this.scenario = new ScenarioManager(this.connection);
    this.csvImport = new CsvImportManager(this.connection);
    this.user = new UserManager(this.connection);
    this.backInStockAlert = new BackInStockAlertManager(this.connection);
    this.tag = new TagManager(this.connection);
    this.waitingNode = new WaitingNodeManager(this.connection);
    this.media = new MediaManager(this.connection);
    this.waiting = new WaitingManager(this.connection);
    this.contact = new ContactManager(this.connection);
    this.messageSkeleton = new MessageSkeletonManager(this.connection);
    this.whatsappWebhook = new WhatsappWebhookManager(this.connection);
    this.sending = new SendingManager(this.connection);
    this.whatsappRule = new WhatsappRuleManager(this.connection);
    this.customer = new CustomerManager(this.connection);
    this.contactTag = new ContactTagManager(this.connection);
    this.condition = new ConditionManager(this.connection);
    this.aiSuggestion = new AiSuggestionManager(this.connection);
    this.gaiaWebhook = new GaiaWebhookManager(this.connection);
    this.stripeWebhook = new StripeWebhookManager(this.connection);
    this.optinFormCompletion = new OptinFormCompletionManager(this.connection);
    this.redirection = new RedirectionManager(this.connection);
    this.replyList = new ReplyListManager(this.connection);
    this.officialWhatsappTemplate = new OfficialWhatsappTemplateManager(this.connection);
    this.aiWebhook = new AiWebhookManager(this.connection);
    this.samsungForm = new SamsungFormManager(this.connection);
    this.productLastUpdate = new ProductLastUpdateManager(this.connection);
    this.aiConfiguration = new AiConfigurationManager(this.connection);
    this.globalVariable = new GlobalVariableManager(this.connection);
    this.urlClickEvent = new UrlClickEventManager(this.connection);
    this.whatsappPartnerWebhook = new WhatsappPartnerWebhookManager(this.connection);
    this.customerStats = new CustomerStatsManager(this.connection);
    this.webhookNode = new WebhookNodeManager(this.connection);
    this.customCouponCodeUsed = new CustomCouponCodeUsedManager(this.connection);
    this.userCustomer = new UserCustomerManager(this.connection);
    this.segmentMembership = new SegmentMembershipManager(this.connection);
    this.segment = new SegmentManager(this.connection);
    this.shopifyDraftOrder = new ShopifyDraftOrderManager(this.connection);
    this.shopifyFile = new ShopifyFileManager(this.connection);
    this.shopifyProductMedia = new ShopifyProductMediaManager(this.connection);
    this.shopifyInventoryItem = new ShopifyInventoryItemManager(this.connection);
    this.shopifyInventoryLevel = new ShopifyInventoryLevelManager(this.connection);
    this.shopifyLocation = new ShopifyLocationManager(this.connection);
    this.flowButton = new FlowButtonManager(this.connection);
    this.flowButtonResponse = new FlowButtonResponseManager(this.connection);
    this.sendingStopped = new SendingStoppedManager(this.connection);
    this.sendingSummary = new SendingSummaryManager(this.connection);
    this.alcmeon = new AlcmeonNamespace(this);
    this.cleaning = new CleaningNamespace(this);
    this.hubspot = new HubspotNamespace(this);
    this.klaviyo = new KlaviyoNamespace(this);
    this.loops = new LoopsNamespace(this);
    this.onboarding = new OnboardingNamespace(this);
    this.recovery = new RecoveryNamespace(this);
    this.settings = new SettingsNamespace(this);
    this.shared = new SharedNamespace(this);
    this.internal = {
      whatsappContact: new InternalModelManager("whatsappContact", this.connection, { "pluralApiIdentifier": "whatsappContacts", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      campaignStats: new InternalModelManager("campaignStats", this.connection, { "pluralApiIdentifier": "campaignStatss", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      customCouponCode: new InternalModelManager("customCouponCode", this.connection, { "pluralApiIdentifier": "customCouponCodes", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      contactStats: new InternalModelManager("contactStats", this.connection, { "pluralApiIdentifier": "contactStatss", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      libraryFlow: new InternalModelManager("libraryFlow", this.connection, { "pluralApiIdentifier": "libraryFlows", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      whatsappTrigger: new InternalModelManager("whatsappTrigger", this.connection, { "pluralApiIdentifier": "whatsappTriggers", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyPlan: new InternalModelManager("shopifyPlan", this.connection, { "pluralApiIdentifier": "shopifyPlans", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      replyButton: new InternalModelManager("replyButton", this.connection, { "pluralApiIdentifier": "replyButtons", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      flowEdge: new InternalModelManager("flowEdge", this.connection, { "pluralApiIdentifier": "flowEdges", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      sendTemplateQueue: new InternalModelManager("sendTemplateQueue", this.connection, { "pluralApiIdentifier": "sendTemplateQueues", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      session: new InternalModelManager("session", this.connection, { "pluralApiIdentifier": "sessions", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      abandonedCartAlert: new InternalModelManager("abandonedCartAlert", this.connection, { "pluralApiIdentifier": "abandonedCartAlerts", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      conversation: new InternalModelManager("conversation", this.connection, { "pluralApiIdentifier": "conversations", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      campaignTemplate: new InternalModelManager("campaignTemplate", this.connection, { "pluralApiIdentifier": "campaignTemplates", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      contactList: new InternalModelManager("contactList", this.connection, { "pluralApiIdentifier": "contactLists", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      campaign: new InternalModelManager("campaign", this.connection, { "pluralApiIdentifier": "campaigns", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      campaignDayStats: new InternalModelManager("campaignDayStats", this.connection, { "pluralApiIdentifier": "campaignDayStatss", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      flowNode: new InternalModelManager("flowNode", this.connection, { "pluralApiIdentifier": "flowNodes", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      whatsappTemplate: new InternalModelManager("whatsappTemplate", this.connection, { "pluralApiIdentifier": "whatsappTemplates", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyAppInstallation: new InternalModelManager("shopifyAppInstallation", this.connection, { "pluralApiIdentifier": "shopifyAppInstallations", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyAppPurchaseOneTime: new InternalModelManager("shopifyAppPurchaseOneTime", this.connection, { "pluralApiIdentifier": "shopifyAppPurchaseOneTimes", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyAppSubscription: new InternalModelManager("shopifyAppSubscription", this.connection, { "pluralApiIdentifier": "shopifyAppSubscriptions", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyCart: new InternalModelManager("shopifyCart", this.connection, { "pluralApiIdentifier": "shopifyCarts", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyCartLineItem: new InternalModelManager("shopifyCartLineItem", this.connection, { "pluralApiIdentifier": "shopifyCartLineItems", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyCheckout: new InternalModelManager("shopifyCheckout", this.connection, { "pluralApiIdentifier": "shopifyCheckouts", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyCheckoutLineItem: new InternalModelManager("shopifyCheckoutLineItem", this.connection, { "pluralApiIdentifier": "shopifyCheckoutLineItems", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyCustomer: new InternalModelManager("shopifyCustomer", this.connection, { "pluralApiIdentifier": "shopifyCustomers", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyFulfillment: new InternalModelManager("shopifyFulfillment", this.connection, { "pluralApiIdentifier": "shopifyFulfillments", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyFulfillmentEvent: new InternalModelManager("shopifyFulfillmentEvent", this.connection, { "pluralApiIdentifier": "shopifyFulfillmentEvents", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyFulfillmentLineItem: new InternalModelManager("shopifyFulfillmentLineItem", this.connection, { "pluralApiIdentifier": "shopifyFulfillmentLineItems", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyGdprRequest: new InternalModelManager("shopifyGdprRequest", this.connection, { "pluralApiIdentifier": "shopifyGdprRequests", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyOrder: new InternalModelManager("shopifyOrder", this.connection, { "pluralApiIdentifier": "shopifyOrders", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyOrderLineItem: new InternalModelManager("shopifyOrderLineItem", this.connection, { "pluralApiIdentifier": "shopifyOrderLineItems", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyProduct: new InternalModelManager("shopifyProduct", this.connection, { "pluralApiIdentifier": "shopifyProducts", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyProductImage: new InternalModelManager("shopifyProductImage", this.connection, { "pluralApiIdentifier": "shopifyProductImages", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyProductVariant: new InternalModelManager("shopifyProductVariant", this.connection, { "pluralApiIdentifier": "shopifyProductVariants", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyShippingAddress: new InternalModelManager("shopifyShippingAddress", this.connection, { "pluralApiIdentifier": "shopifyShippingAddresses", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyShop: new InternalModelManager("shopifyShop", this.connection, { "pluralApiIdentifier": "shopifyShops", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifySync: new InternalModelManager("shopifySync", this.connection, { "pluralApiIdentifier": "shopifySyncs", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      message: new InternalModelManager("message", this.connection, { "pluralApiIdentifier": "messages", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      scenario: new InternalModelManager("scenario", this.connection, { "pluralApiIdentifier": "scenarios", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      csvImport: new InternalModelManager("csvImport", this.connection, { "pluralApiIdentifier": "csvImports", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      user: new InternalModelManager("user", this.connection, { "pluralApiIdentifier": "users", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      backInStockAlert: new InternalModelManager("backInStockAlert", this.connection, { "pluralApiIdentifier": "backInStockAlerts", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      tag: new InternalModelManager("tag", this.connection, { "pluralApiIdentifier": "tags", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      waitingNode: new InternalModelManager("waitingNode", this.connection, { "pluralApiIdentifier": "waitingNodes", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      media: new InternalModelManager("media", this.connection, { "pluralApiIdentifier": "medias", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      waiting: new InternalModelManager("waiting", this.connection, { "pluralApiIdentifier": "waitings", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      contact: new InternalModelManager("contact", this.connection, { "pluralApiIdentifier": "contacts", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      messageSkeleton: new InternalModelManager("messageSkeleton", this.connection, { "pluralApiIdentifier": "messageSkeletons", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      whatsappWebhook: new InternalModelManager("whatsappWebhook", this.connection, { "pluralApiIdentifier": "whatsappWebhooks", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      sending: new InternalModelManager("sending", this.connection, { "pluralApiIdentifier": "sendings", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      whatsappRule: new InternalModelManager("whatsappRule", this.connection, { "pluralApiIdentifier": "whatsappRules", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      customer: new InternalModelManager("customer", this.connection, { "pluralApiIdentifier": "customers", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      contactTag: new InternalModelManager("contactTag", this.connection, { "pluralApiIdentifier": "contactTags", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      condition: new InternalModelManager("condition", this.connection, { "pluralApiIdentifier": "conditions", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      aiSuggestion: new InternalModelManager("aiSuggestion", this.connection, { "pluralApiIdentifier": "aiSuggestions", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      gaiaWebhook: new InternalModelManager("gaiaWebhook", this.connection, { "pluralApiIdentifier": "gaiaWebhooks", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      stripeWebhook: new InternalModelManager("stripeWebhook", this.connection, { "pluralApiIdentifier": "stripeWebhooks", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      optinFormCompletion: new InternalModelManager("optinFormCompletion", this.connection, { "pluralApiIdentifier": "optinFormCompletions", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      redirection: new InternalModelManager("redirection", this.connection, { "pluralApiIdentifier": "redirections", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      replyList: new InternalModelManager("replyList", this.connection, { "pluralApiIdentifier": "replyLists", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      officialWhatsappTemplate: new InternalModelManager("officialWhatsappTemplate", this.connection, { "pluralApiIdentifier": "officialWhatsappTemplates", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      aiWebhook: new InternalModelManager("aiWebhook", this.connection, { "pluralApiIdentifier": "aiWebhooks", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      samsungForm: new InternalModelManager("samsungForm", this.connection, { "pluralApiIdentifier": "samsungForms", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      productLastUpdate: new InternalModelManager("productLastUpdate", this.connection, { "pluralApiIdentifier": "productLastUpdates", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      aiConfiguration: new InternalModelManager("aiConfiguration", this.connection, { "pluralApiIdentifier": "aiConfigurations", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      globalVariable: new InternalModelManager("globalVariable", this.connection, { "pluralApiIdentifier": "globalVariables", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      urlClickEvent: new InternalModelManager("urlClickEvent", this.connection, { "pluralApiIdentifier": "urlClickEvents", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      whatsappPartnerWebhook: new InternalModelManager("whatsappPartnerWebhook", this.connection, { "pluralApiIdentifier": "whatsappPartnerWebhooks", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      customerStats: new InternalModelManager("customerStats", this.connection, { "pluralApiIdentifier": "customerStatss", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      webhookNode: new InternalModelManager("webhookNode", this.connection, { "pluralApiIdentifier": "webhookNodes", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      customCouponCodeUsed: new InternalModelManager("customCouponCodeUsed", this.connection, { "pluralApiIdentifier": "customCouponCodeUseds", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      userCustomer: new InternalModelManager("userCustomer", this.connection, { "pluralApiIdentifier": "userCustomers", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      segmentMembership: new InternalModelManager("segmentMembership", this.connection, { "pluralApiIdentifier": "segmentMemberships", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      segment: new InternalModelManager("segment", this.connection, { "pluralApiIdentifier": "segments", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyDraftOrder: new InternalModelManager("shopifyDraftOrder", this.connection, { "pluralApiIdentifier": "shopifyDraftOrders", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyFile: new InternalModelManager("shopifyFile", this.connection, { "pluralApiIdentifier": "shopifyFiles", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyProductMedia: new InternalModelManager("shopifyProductMedia", this.connection, { "pluralApiIdentifier": "shopifyProductMedias", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyInventoryItem: new InternalModelManager("shopifyInventoryItem", this.connection, { "pluralApiIdentifier": "shopifyInventoryItems", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyInventoryLevel: new InternalModelManager("shopifyInventoryLevel", this.connection, { "pluralApiIdentifier": "shopifyInventoryLevels", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyLocation: new InternalModelManager("shopifyLocation", this.connection, { "pluralApiIdentifier": "shopifyLocations", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      flowButton: new InternalModelManager("flowButton", this.connection, { "pluralApiIdentifier": "flowButtons", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      flowButtonResponse: new InternalModelManager("flowButtonResponse", this.connection, { "pluralApiIdentifier": "flowButtonResponses", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      sendingStopped: new InternalModelManager("sendingStopped", this.connection, { "pluralApiIdentifier": "sendingStoppeds", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      sendingSummary: new InternalModelManager("sendingSummary", this.connection, { "pluralApiIdentifier": "sendingSummaries", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      alcmeon: {},
      cleaning: {},
      hubspot: {},
      klaviyo: {},
      loops: {},
      onboarding: {},
      recovery: {
        data: {},
        shared: {}
      },
      settings: {},
      shared: {}
    };
  }
  /**
   * Returns a new Client instance that will call the Gadget API as the application's admin user.
   * This can only be used for API clients using internal authentication.
   * @returns {GetsimioClient} A new GetsimioClient instance with admin authentication
   */
  get actAsAdmin() {
    assert(this.options?.authenticationMode?.internal, `actAsAdmin can only be used for API clients using internal authentication, this client is using ${JSON.stringify(this.options?.authenticationMode)}`);
    return new GetsimioClient({
      ...this.options,
      authenticationMode: {
        internal: {
          ...this.options.authenticationMode.internal,
          actAsSession: false
        }
      }
    });
  }
  /**
   * Returns a new GetsimioClient instance that will call the Gadget API as with the permission of the current session.
   * This can only be used for API clients using internal authentication.
   * @returns {GetsimioClient} A new GetsimioClient instance with session authentication
   */
  get actAsSession() {
    assert(this.options?.authenticationMode?.internal, "actAsSession can only be used for API clients using internal authentication");
    return new GetsimioClient({
      ...this.options,
      authenticationMode: {
        internal: {
          ...this.options.authenticationMode.internal,
          actAsSession: true
        }
      }
    });
  }
  /** Run an arbitrary GraphQL query. */
  async query(graphQL, variables, options) {
    const { data, error } = await this.connection.currentClient.query(graphQL, variables, options).toPromise();
    if (error)
      throw error;
    return data;
  }
  /** Run an arbitrary GraphQL mutation. */
  async mutate(graphQL, variables, options) {
    const { data, error } = await this.connection.currentClient.mutation(graphQL, variables, options).toPromise();
    if (error)
      throw error;
    return data;
  }
  /**
   * `fetch` function that works the same as the built-in `fetch` function, but automatically passes authentication information for this API client.
   *
   * @example
   * await api.fetch("https://myapp--development.gadget.app/foo/bar");
   *
   * @example
   * // fetch a relative URL from the endpoint this API client is configured to fetch from
   * await api.fetch("/foo/bar");
   **/
  async fetch(input, init = {}) {
    return await this.connection.fetch(input, init);
  }
  async enqueue(action, inputOrOptions, maybeOptions) {
    assert(action, ".enqueue must be passed an action as the first argument but was passed undefined");
    let input;
    let options;
    if (typeof maybeOptions !== "undefined") {
      if (typeof inputOrOptions == "string") {
        input = { id: inputOrOptions };
      } else {
        input = inputOrOptions;
      }
      options = maybeOptions;
    } else if (!action.variables || Object.keys(action.variables).length == 0) {
      input = {};
      options = inputOrOptions;
    } else {
      if (typeof inputOrOptions == "string") {
        input = { id: inputOrOptions };
      } else {
        input = inputOrOptions;
      }
      options = {};
    }
    return await enqueueActionRunner(this.connection, action, input, options);
  }
  /**
   * Returns a handle for a given background action id
   *
   * @param action The action that was enqueued
   * @param id The id of the background action
   *
   * @example
   * const handle = api.handle(api.widget.update, "app-job-12346");
   *
   * @example
   * const handle = api.handle(api.someGlobalAction, "app-job-56789");
   **/
  handle(action, id) {
    return new BackgroundActionHandle(this.connection, action, id);
  }
  toString() {
    return `GetsimioClient<${this.environment}>`;
  }
  toJSON() {
    return this.toString();
  }
}
GetsimioClient.prototype[$modelRelationships] = { "whatsappContact": { "customer": { "type": "BelongsTo", "model": "customer" } }, "campaignStats": { "campaign": { "type": "BelongsTo", "model": "campaign" } }, "customCouponCode": { "customer": { "type": "BelongsTo", "model": "customer" }, "contact": { "type": "BelongsTo", "model": "contact" } }, "contactStats": { "contact": { "type": "BelongsTo", "model": "contact" }, "customer": { "type": "BelongsTo", "model": "customer" } }, "libraryFlow": { "originalScenario": { "type": "BelongsTo", "model": "scenario" } }, "whatsappTrigger": { "flowNode": { "type": "BelongsTo", "model": "flowNode" }, "customer": { "type": "BelongsTo", "model": "customer" } }, "shopifyPlan": { "shopifyShop": { "type": "HasMany", "model": "shopifyShop" } }, "replyButton": { "messageSkeleton": { "type": "BelongsTo", "model": "messageSkeleton" }, "flowNode": { "type": "BelongsTo", "model": "flowNode" }, "customer": { "type": "BelongsTo", "model": "customer" }, "whatsappRules": { "type": "HasMany", "model": "whatsappRule" }, "messages": { "type": "HasMany", "model": "message" } }, "flowEdge": { "sourceNode": { "type": "BelongsTo", "model": "flowNode" }, "scenario": { "type": "BelongsTo", "model": "scenario" }, "targetNode": { "type": "BelongsTo", "model": "flowNode" } }, "sendTemplateQueue": {}, "session": { "shop": { "type": "BelongsTo", "model": "shopifyShop" }, "user": { "type": "BelongsTo", "model": "user" }, "customer": { "type": "BelongsTo", "model": "customer" } }, "abandonedCartAlert": { "customer": { "type": "BelongsTo", "model": "customer" }, "checkout": { "type": "BelongsTo", "model": "shopifyCheckout" }, "campaign": { "type": "BelongsTo", "model": "campaign" } }, "conversation": { "lastGaiaFlowNode": { "type": "BelongsTo", "model": "flowNode" }, "messages": { "type": "HasMany", "model": "message" }, "attributedUser": { "type": "BelongsTo", "model": "user" }, "contact": { "type": "BelongsTo", "model": "contact" }, "customer": { "type": "BelongsTo", "model": "customer" }, "flowButtonResponses": { "type": "HasMany", "model": "flowButtonResponse" } }, "campaignTemplate": {}, "contactList": { "customer": { "type": "BelongsTo", "model": "customer" }, "sendings": { "type": "HasMany", "model": "sending" } }, "campaign": { "firstMessageSkeleton": { "type": "BelongsTo", "model": "messageSkeleton" }, "whatsappTemplate": { "type": "BelongsTo", "model": "whatsappTemplate" }, "messageSkeletons": { "type": "HasMany", "model": "messageSkeleton" }, "shopifyOrders": { "type": "HasMany", "model": "shopifyOrder" }, "customer": { "type": "BelongsTo", "model": "customer" }, "sendings": { "type": "HasMany", "model": "sending" }, "whatsappRules": { "type": "HasMany", "model": "whatsappRule" }, "backInStockAlerts": { "type": "HasMany", "model": "backInStockAlert" }, "scenario": { "type": "HasOne", "model": "scenario" }, "messages": { "type": "HasMany", "model": "message" }, "campaignStats": { "type": "HasOne", "model": "campaignStats" }, "libraryFlow": { "type": "BelongsTo", "model": "libraryFlow" }, "flowButtonResponses": { "type": "HasMany", "model": "flowButtonResponse" }, "sendingStoppeds": { "type": "HasMany", "model": "sendingStopped" }, "sendingSummaries": { "type": "HasMany", "model": "sendingSummary" } }, "campaignDayStats": { "customer": { "type": "BelongsTo", "model": "customer" }, "campaign": { "type": "BelongsTo", "model": "campaign" } }, "flowNode": { "aiConfiguration": { "type": "HasOne", "model": "aiConfiguration" }, "whatsappTrigger": { "type": "HasOne", "model": "whatsappTrigger" }, "conversations": { "type": "HasMany", "model": "conversation" }, "flowNodes": { "type": "HasMany", "model": "flowNode" }, "whatsappTemplate": { "type": "HasOne", "model": "whatsappTemplate" }, "customer": { "type": "BelongsTo", "model": "customer" }, "flowEdges": { "type": "HasMany", "model": "flowEdge" }, "scenario": { "type": "BelongsTo", "model": "scenario" }, "parentNode": { "type": "BelongsTo", "model": "flowNode" }, "replyButton": { "type": "HasOne", "model": "replyButton" }, "messageSkeleton": { "type": "HasOne", "model": "messageSkeleton" }, "waitingNode": { "type": "HasOne", "model": "waitingNode" }, "tag": { "type": "BelongsTo", "model": "tag" }, "replyList": { "type": "HasOne", "model": "replyList" }, "condition": { "type": "HasOne", "model": "condition" }, "webhookNode": { "type": "HasOne", "model": "webhookNode" }, "waitings": { "type": "HasMany", "model": "waiting" }, "redirection": { "type": "HasOne", "model": "redirection" }, "flowButton": { "type": "HasOne", "model": "flowButton" } }, "whatsappTemplate": { "redirectionUrl": { "type": "BelongsTo", "model": "redirection" }, "messageSkeleton": { "type": "HasOne", "model": "messageSkeleton" }, "messages": { "type": "HasMany", "model": "message" }, "flowNode": { "type": "BelongsTo", "model": "flowNode" }, "whatsappRules": { "type": "HasMany", "model": "whatsappRule" }, "campaign": { "type": "HasOne", "model": "campaign" }, "customer": { "type": "BelongsTo", "model": "customer" }, "redirectionUrl2": { "type": "BelongsTo", "model": "redirection" } }, "shopifyAppInstallation": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyAppPurchaseOneTime": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyAppSubscription": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyCart": { "lineItems": { "type": "HasMany", "model": "shopifyCartLineItem" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyCartLineItem": { "cart": { "type": "BelongsTo", "model": "shopifyCart" }, "product": { "type": "BelongsTo", "model": "shopifyProduct" }, "variant": { "type": "BelongsTo", "model": "shopifyProductVariant" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyCheckout": { "lineItems": { "type": "HasMany", "model": "shopifyCheckoutLineItem" }, "customer": { "type": "BelongsTo", "model": "shopifyCustomer" }, "shippingAddress": { "type": "BelongsTo", "model": "shopifyShippingAddress" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" }, "order": { "type": "BelongsTo", "model": "shopifyOrder" }, "location": { "type": "BelongsTo", "model": "shopifyLocation" } }, "shopifyCheckoutLineItem": { "checkout": { "type": "BelongsTo", "model": "shopifyCheckout" }, "product": { "type": "BelongsTo", "model": "shopifyProduct" }, "variant": { "type": "BelongsTo", "model": "shopifyProductVariant" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyCustomer": { "checkouts": { "type": "HasMany", "model": "shopifyCheckout" }, "orders": { "type": "HasMany", "model": "shopifyOrder" }, "lastOrder": { "type": "BelongsTo", "model": "shopifyOrder" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" }, "contact": { "type": "HasOne", "model": "contact" }, "draftOrders": { "type": "HasMany", "model": "shopifyDraftOrder" } }, "shopifyFulfillment": { "fulfillmentEvents": { "type": "HasMany", "model": "shopifyFulfillmentEvent" }, "fulfillmentLineItems": { "type": "HasMany", "model": "shopifyFulfillmentLineItem" }, "order": { "type": "BelongsTo", "model": "shopifyOrder" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" }, "location": { "type": "BelongsTo", "model": "shopifyLocation" } }, "shopifyFulfillmentEvent": { "fulfillment": { "type": "BelongsTo", "model": "shopifyFulfillment" }, "order": { "type": "BelongsTo", "model": "shopifyOrder" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyFulfillmentLineItem": { "fulfillment": { "type": "BelongsTo", "model": "shopifyFulfillment" }, "orderLineItem": { "type": "BelongsTo", "model": "shopifyOrderLineItem" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyGdprRequest": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyOrder": { "fulfillmentEvents": { "type": "HasMany", "model": "shopifyFulfillmentEvent" }, "fulfillments": { "type": "HasMany", "model": "shopifyFulfillment" }, "checkouts": { "type": "HasMany", "model": "shopifyCheckout" }, "lineItems": { "type": "HasMany", "model": "shopifyOrderLineItem" }, "customer": { "type": "BelongsTo", "model": "shopifyCustomer" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" }, "campaignOrigin": { "type": "BelongsTo", "model": "campaign" }, "draftOrder": { "type": "HasOne", "model": "shopifyDraftOrder" }, "location": { "type": "BelongsTo", "model": "shopifyLocation" }, "retailLocation": { "type": "BelongsTo", "model": "shopifyLocation" } }, "shopifyOrderLineItem": { "fulfillmentLineItems": { "type": "HasMany", "model": "shopifyFulfillmentLineItem" }, "order": { "type": "BelongsTo", "model": "shopifyOrder" }, "product": { "type": "BelongsTo", "model": "shopifyProduct" }, "variant": { "type": "BelongsTo", "model": "shopifyProductVariant" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" }, "originLocation": { "type": "BelongsTo", "model": "shopifyLocation" } }, "shopifyProduct": { "cartLineItems": { "type": "HasMany", "model": "shopifyCartLineItem" }, "orderLineItems": { "type": "HasMany", "model": "shopifyOrderLineItem" }, "images": { "type": "HasMany", "model": "shopifyProductImage" }, "checkoutLineItems": { "type": "HasMany", "model": "shopifyCheckoutLineItem" }, "variants": { "type": "HasMany", "model": "shopifyProductVariant" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" }, "featuredMedia": { "type": "HasOne", "model": "shopifyProductMedia" }, "shopifyProductMedias": { "type": "HasMany", "model": "shopifyProductMedia" }, "media": { "type": "HasManyThrough", "model": "shopifyFile", "through": "shopifyProductMedia" } }, "shopifyProductImage": { "variants": { "type": "HasMany", "model": "shopifyProductVariant" }, "product": { "type": "BelongsTo", "model": "shopifyProduct" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyProductVariant": { "cartLineItems": { "type": "HasMany", "model": "shopifyCartLineItem" }, "checkoutLineItems": { "type": "HasMany", "model": "shopifyCheckoutLineItem" }, "orderLineItems": { "type": "HasMany", "model": "shopifyOrderLineItem" }, "sendings": { "type": "HasMany", "model": "sending" }, "product": { "type": "BelongsTo", "model": "shopifyProduct" }, "productImage": { "type": "BelongsTo", "model": "shopifyProductImage" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" }, "backInStockAlerts": { "type": "HasMany", "model": "backInStockAlert" }, "inventoryItem": { "type": "BelongsTo", "model": "shopifyInventoryItem" } }, "shopifyShippingAddress": { "checkouts": { "type": "HasMany", "model": "shopifyCheckout" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyShop": { "customer": { "type": "HasOne", "model": "customer" }, "appInstallations": { "type": "HasMany", "model": "shopifyAppInstallation" }, "orders": { "type": "HasMany", "model": "shopifyOrder" }, "syncs": { "type": "HasMany", "model": "shopifySync" }, "orderLineItems": { "type": "HasMany", "model": "shopifyOrderLineItem" }, "cartLineItems": { "type": "HasMany", "model": "shopifyCartLineItem" }, "fulfillments": { "type": "HasMany", "model": "shopifyFulfillment" }, "checkouts": { "type": "HasMany", "model": "shopifyCheckout" }, "productVariants": { "type": "HasMany", "model": "shopifyProductVariant" }, "appSubscriptions": { "type": "HasMany", "model": "shopifyAppSubscription" }, "productImages": { "type": "HasMany", "model": "shopifyProductImage" }, "checkoutLineItems": { "type": "HasMany", "model": "shopifyCheckoutLineItem" }, "shippingAddresses": { "type": "HasMany", "model": "shopifyShippingAddress" }, "fulfillmentEvents": { "type": "HasMany", "model": "shopifyFulfillmentEvent" }, "customers": { "type": "HasMany", "model": "shopifyCustomer" }, "appPurchaseOneTimes": { "type": "HasMany", "model": "shopifyAppPurchaseOneTime" }, "fulfillmentLineItems": { "type": "HasMany", "model": "shopifyFulfillmentLineItem" }, "gdprRequests": { "type": "HasMany", "model": "shopifyGdprRequest" }, "carts": { "type": "HasMany", "model": "shopifyCart" }, "products": { "type": "HasMany", "model": "shopifyProduct" }, "plan": { "type": "BelongsTo", "model": "shopifyPlan" }, "draftOrders": { "type": "HasMany", "model": "shopifyDraftOrder" }, "files": { "type": "HasMany", "model": "shopifyFile" }, "productMedia": { "type": "HasMany", "model": "shopifyProductMedia" }, "inventoryItems": { "type": "HasMany", "model": "shopifyInventoryItem" }, "inventoryLevels": { "type": "HasMany", "model": "shopifyInventoryLevel" }, "locations": { "type": "HasMany", "model": "shopifyLocation" }, "primaryLocation": { "type": "BelongsTo", "model": "shopifyLocation" } }, "shopifySync": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "message": { "customer": { "type": "BelongsTo", "model": "customer" }, "campaign": { "type": "BelongsTo", "model": "campaign" }, "replyButton": { "type": "BelongsTo", "model": "replyButton" }, "scenario": { "type": "BelongsTo", "model": "scenario" }, "conversation": { "type": "BelongsTo", "model": "conversation" }, "whatsappTemplate": { "type": "BelongsTo", "model": "whatsappTemplate" }, "messageSkeleton": { "type": "BelongsTo", "model": "messageSkeleton" }, "attributedShopifyOrder": { "type": "BelongsTo", "model": "shopifyOrder" }, "replyList": { "type": "BelongsTo", "model": "replyList" }, "contact": { "type": "BelongsTo", "model": "contact" }, "flowButtonResponse": { "type": "HasOne", "model": "flowButtonResponse" } }, "scenario": { "campaign": { "type": "BelongsTo", "model": "campaign" }, "rootNode": { "type": "BelongsTo", "model": "flowNode" }, "contacts": { "type": "HasMany", "model": "contact" }, "flowEdges": { "type": "HasMany", "model": "flowEdge" }, "customer": { "type": "BelongsTo", "model": "customer" }, "whatsappRules": { "type": "HasMany", "model": "whatsappRule" }, "messages": { "type": "HasMany", "model": "message" }, "flowNodes": { "type": "HasMany", "model": "flowNode" }, "urlClickEvents": { "type": "HasMany", "model": "urlClickEvent" }, "optoutContacts": { "type": "HasMany", "model": "contact" }, "flowButtonResponses": { "type": "HasMany", "model": "flowButtonResponse" } }, "csvImport": { "customer": { "type": "BelongsTo", "model": "customer" }, "tag": { "type": "BelongsTo", "model": "tag" } }, "user": { "customer": { "type": "BelongsTo", "model": "customer" }, "userCustomers": { "type": "HasMany", "model": "userCustomer" } }, "backInStockAlert": { "shopifyProductVariant": { "type": "BelongsTo", "model": "shopifyProductVariant" }, "contact": { "type": "BelongsTo", "model": "contact" }, "campaign": { "type": "BelongsTo", "model": "campaign" }, "customer": { "type": "BelongsTo", "model": "customer" } }, "tag": { "contactTags": { "type": "HasMany", "model": "contactTag" }, "contacts": { "type": "HasManyThrough", "model": "contact", "through": "contactTag" }, "customer": { "type": "BelongsTo", "model": "customer" } }, "waitingNode": { "flowNode": { "type": "BelongsTo", "model": "flowNode" }, "customer": { "type": "BelongsTo", "model": "customer" } }, "media": { "customer": { "type": "BelongsTo", "model": "customer" } }, "waiting": { "flowNode": { "type": "BelongsTo", "model": "flowNode" }, "customer": { "type": "BelongsTo", "model": "customer" } }, "contact": { "attributedUser": { "type": "BelongsTo", "model": "user" }, "contactStats": { "type": "HasOne", "model": "contactStats" }, "optInSource": { "type": "BelongsTo", "model": "scenario" }, "shopifyCustomer": { "type": "BelongsTo", "model": "shopifyCustomer" }, "customer": { "type": "BelongsTo", "model": "customer" }, "conversations": { "type": "HasMany", "model": "conversation" }, "backInStockAlerts": { "type": "HasMany", "model": "backInStockAlert" }, "contactTags": { "type": "HasMany", "model": "contactTag" }, "tags": { "type": "HasManyThrough", "model": "tag", "through": "contactTag" }, "optoutScenario": { "type": "BelongsTo", "model": "scenario" }, "urlClickEvents": { "type": "HasMany", "model": "urlClickEvent" }, "messages": { "type": "HasMany", "model": "message" }, "segmentMemberships": { "type": "HasMany", "model": "segmentMembership" }, "segments": { "type": "HasManyThrough", "model": "segment", "through": "segmentMembership" }, "flowButtonResponses": { "type": "HasMany", "model": "flowButtonResponse" } }, "messageSkeleton": { "replyLists": { "type": "HasMany", "model": "replyList" }, "redirectionUrl": { "type": "BelongsTo", "model": "redirection" }, "sourceWhatsappRule": { "type": "BelongsTo", "model": "whatsappRule" }, "campaign": { "type": "BelongsTo", "model": "campaign" }, "triggeredWhatsappRule": { "type": "BelongsTo", "model": "whatsappRule" }, "whatsappTemplate": { "type": "BelongsTo", "model": "whatsappTemplate" }, "customer": { "type": "BelongsTo", "model": "customer" }, "flowNode": { "type": "BelongsTo", "model": "flowNode" }, "replyButtons": { "type": "HasMany", "model": "replyButton" }, "flowButton": { "type": "HasOne", "model": "flowButton" } }, "whatsappWebhook": { "customer": { "type": "BelongsTo", "model": "customer" } }, "sending": { "shopifyFulfillment": { "type": "BelongsTo", "model": "shopifyFulfillment" }, "campaign": { "type": "BelongsTo", "model": "campaign" }, "productVariant": { "type": "BelongsTo", "model": "shopifyProductVariant" }, "shopifyOrder": { "type": "BelongsTo", "model": "shopifyOrder" }, "customer": { "type": "BelongsTo", "model": "customer" }, "contactListSegment": { "type": "BelongsTo", "model": "contactList" }, "segment": { "type": "BelongsTo", "model": "segment" } }, "whatsappRule": { "customer": { "type": "BelongsTo", "model": "customer" }, "whatsappTemplate": { "type": "BelongsTo", "model": "whatsappTemplate" }, "campaign": { "type": "BelongsTo", "model": "campaign" }, "actionMessageSkeleton": { "type": "HasOne", "model": "messageSkeleton" }, "eventMessageSkeleton": { "type": "HasOne", "model": "messageSkeleton" }, "scenario": { "type": "BelongsTo", "model": "scenario" }, "eventReplyButton": { "type": "BelongsTo", "model": "replyButton" } }, "customer": { "customerStats": { "type": "HasOne", "model": "customerStats" }, "automaticReplyNode": { "type": "BelongsTo", "model": "flowNode" }, "campaigns": { "type": "HasMany", "model": "campaign" }, "tags": { "type": "HasMany", "model": "tag" }, "abandonedCartCampaign": { "type": "BelongsTo", "model": "campaign" }, "sendings": { "type": "HasMany", "model": "sending" }, "optinConfirmationCampaign": { "type": "BelongsTo", "model": "campaign" }, "messageSkeletons": { "type": "HasMany", "model": "messageSkeleton" }, "orderConfirmationCampaign": { "type": "BelongsTo", "model": "campaign" }, "scenarios": { "type": "HasMany", "model": "scenario" }, "backInStockCampaign": { "type": "BelongsTo", "model": "campaign" }, "orderTrackingCampaign": { "type": "BelongsTo", "model": "campaign" }, "flowNodes": { "type": "HasMany", "model": "flowNode" }, "conversations": { "type": "HasMany", "model": "conversation" }, "whatsappRules": { "type": "HasMany", "model": "whatsappRule" }, "orderConfirmationCampaignWithoutOptin": { "type": "BelongsTo", "model": "campaign" }, "whatsappTemplates": { "type": "HasMany", "model": "whatsappTemplate" }, "backInStockAlerts": { "type": "HasMany", "model": "backInStockAlert" }, "replyButtons": { "type": "HasMany", "model": "replyButton" }, "optIns": { "type": "HasMany", "model": "contact" }, "replyLists": { "type": "HasMany", "model": "replyList" }, "contactStats": { "type": "HasMany", "model": "contactStats" }, "copilotAiConfiguration": { "type": "BelongsTo", "model": "aiConfiguration" }, "webhookNode": { "type": "HasOne", "model": "webhookNode" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" }, "users": { "type": "HasMany", "model": "user" }, "userCustomers": { "type": "HasMany", "model": "userCustomer" }, "segments": { "type": "HasMany", "model": "segment" }, "segmentMemberships": { "type": "HasMany", "model": "segmentMembership" }, "whatsappOrderNode": { "type": "BelongsTo", "model": "flowNode" }, "flowButtonResponses": { "type": "HasMany", "model": "flowButtonResponse" }, "sendingStoppeds": { "type": "HasMany", "model": "sendingStopped" }, "sendingSummaries": { "type": "HasMany", "model": "sendingSummary" } }, "contactTag": { "contact": { "type": "BelongsTo", "model": "contact" }, "tag": { "type": "BelongsTo", "model": "tag" } }, "condition": { "flowNode": { "type": "BelongsTo", "model": "flowNode" }, "customer": { "type": "BelongsTo", "model": "customer" }, "valueTag": { "type": "BelongsTo", "model": "tag" } }, "aiSuggestion": { "conversation": { "type": "BelongsTo", "model": "conversation" }, "customer": { "type": "BelongsTo", "model": "customer" }, "aiConfiguration": { "type": "BelongsTo", "model": "aiConfiguration" }, "contact": { "type": "BelongsTo", "model": "contact" } }, "gaiaWebhook": { "customer": { "type": "BelongsTo", "model": "customer" } }, "stripeWebhook": { "customer": { "type": "BelongsTo", "model": "customer" } }, "optinFormCompletion": { "customer": { "type": "BelongsTo", "model": "customer" } }, "redirection": { "customer": { "type": "BelongsTo", "model": "customer" }, "messageSkeleton": { "type": "HasOne", "model": "messageSkeleton" }, "flowNode": { "type": "BelongsTo", "model": "flowNode" } }, "replyList": { "messages": { "type": "HasMany", "model": "message" }, "flowNode": { "type": "BelongsTo", "model": "flowNode" }, "messageSkeleton": { "type": "BelongsTo", "model": "messageSkeleton" }, "customer": { "type": "BelongsTo", "model": "customer" } }, "officialWhatsappTemplate": { "customer": { "type": "BelongsTo", "model": "customer" } }, "aiWebhook": { "customer": { "type": "BelongsTo", "model": "customer" } }, "samsungForm": {}, "productLastUpdate": {}, "aiConfiguration": { "customer": { "type": "BelongsTo", "model": "customer" }, "flowNode": { "type": "BelongsTo", "model": "flowNode" } }, "globalVariable": {}, "urlClickEvent": { "customer": { "type": "BelongsTo", "model": "customer" }, "messageSkeleton": { "type": "BelongsTo", "model": "messageSkeleton" }, "scenario": { "type": "BelongsTo", "model": "scenario" }, "redirection": { "type": "BelongsTo", "model": "redirection" }, "contact": { "type": "BelongsTo", "model": "contact" } }, "whatsappPartnerWebhook": {}, "customerStats": { "customer": { "type": "BelongsTo", "model": "customer" } }, "webhookNode": { "customer": { "type": "BelongsTo", "model": "customer" }, "flowNode": { "type": "BelongsTo", "model": "flowNode" } }, "customCouponCodeUsed": { "contact": { "type": "BelongsTo", "model": "contact" }, "customer": { "type": "BelongsTo", "model": "customer" } }, "userCustomer": { "user": { "type": "BelongsTo", "model": "user" }, "customer": { "type": "BelongsTo", "model": "customer" } }, "segmentMembership": { "contact": { "type": "BelongsTo", "model": "contact" }, "customer": { "type": "BelongsTo", "model": "customer" }, "segment": { "type": "BelongsTo", "model": "segment" } }, "segment": { "customer": { "type": "BelongsTo", "model": "customer" }, "segmentMemberships": { "type": "HasMany", "model": "segmentMembership" }, "contacts": { "type": "HasManyThrough", "model": "contact", "through": "segmentMembership" }, "sendings": { "type": "HasMany", "model": "sending" } }, "shopifyDraftOrder": { "customer": { "type": "BelongsTo", "model": "shopifyCustomer" }, "order": { "type": "BelongsTo", "model": "shopifyOrder" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyFile": { "shopifyProductMedias": { "type": "HasMany", "model": "shopifyProductMedia" }, "product": { "type": "HasManyThrough", "model": "shopifyProduct", "through": "shopifyProductMedia" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyProductMedia": { "featuredMediaForProduct": { "type": "BelongsTo", "model": "shopifyProduct" }, "file": { "type": "BelongsTo", "model": "shopifyFile" }, "product": { "type": "BelongsTo", "model": "shopifyProduct" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyInventoryItem": { "shopifyInventoryLevels": { "type": "HasMany", "model": "shopifyInventoryLevel" }, "locations": { "type": "HasManyThrough", "model": "shopifyLocation", "through": "shopifyInventoryLevel" }, "productVariant": { "type": "HasOne", "model": "shopifyProductVariant" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" }, "productVariants": { "type": "HasMany", "model": "shopifyProductVariant" } }, "shopifyInventoryLevel": { "inventoryItem": { "type": "BelongsTo", "model": "shopifyInventoryItem" }, "location": { "type": "BelongsTo", "model": "shopifyLocation" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyLocation": { "shopifyInventoryLevels": { "type": "HasMany", "model": "shopifyInventoryLevel" }, "inventoryItems": { "type": "HasManyThrough", "model": "shopifyInventoryItem", "through": "shopifyInventoryLevel" }, "checkouts": { "type": "HasMany", "model": "shopifyCheckout" }, "fulfillments": { "type": "HasMany", "model": "shopifyFulfillment" }, "orderLineItems": { "type": "HasMany", "model": "shopifyOrderLineItem" }, "orders": { "type": "HasMany", "model": "shopifyOrder" }, "retailOrders": { "type": "HasMany", "model": "shopifyOrder" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "flowButton": { "customer": { "type": "BelongsTo", "model": "customer" }, "flowNode": { "type": "BelongsTo", "model": "flowNode" }, "flowButtonResponses": { "type": "HasMany", "model": "flowButtonResponse" }, "messageSkeleton": { "type": "BelongsTo", "model": "messageSkeleton" } }, "flowButtonResponse": { "flowButton": { "type": "BelongsTo", "model": "flowButton" }, "campaign": { "type": "BelongsTo", "model": "campaign" }, "contact": { "type": "BelongsTo", "model": "contact" }, "conversation": { "type": "BelongsTo", "model": "conversation" }, "scenario": { "type": "BelongsTo", "model": "scenario" }, "message": { "type": "BelongsTo", "model": "message" }, "customer": { "type": "BelongsTo", "model": "customer" } }, "sendingStopped": { "customer": { "type": "BelongsTo", "model": "customer" }, "campaign": { "type": "BelongsTo", "model": "campaign" } }, "sendingSummary": { "campaign": { "type": "BelongsTo", "model": "campaign" }, "customer": { "type": "BelongsTo", "model": "customer" } } };
const coreImplementation = {
  GadgetRecord,
  disambiguateActionVariables,
  disambiguateBulkActionVariables,
  capitalizeIdentifier,
  wrapClientSideError: ErrorWrapper.forClientSideError,
  errorIfDataAbsent: ErrorWrapper.errorIfDataAbsent,
  namespaceDataPath,
  camelize,
  isEqual
};
GetsimioClient.prototype[$coreImplementation] = coreImplementation;
const Client = GetsimioClient;
export {
  Client,
  DefaultAbandonedCartAlertSelection2 as DefaultAbandonedCartAlertSelection,
  DefaultAiConfigurationSelection2 as DefaultAiConfigurationSelection,
  DefaultAiSuggestionSelection2 as DefaultAiSuggestionSelection,
  DefaultAiWebhookSelection2 as DefaultAiWebhookSelection,
  DefaultBackInStockAlertSelection2 as DefaultBackInStockAlertSelection,
  DefaultCampaignDayStatsSelection2 as DefaultCampaignDayStatsSelection,
  DefaultCampaignSelection2 as DefaultCampaignSelection,
  DefaultCampaignStatsSelection2 as DefaultCampaignStatsSelection,
  DefaultCampaignTemplateSelection2 as DefaultCampaignTemplateSelection,
  DefaultConditionSelection2 as DefaultConditionSelection,
  DefaultContactListSelection2 as DefaultContactListSelection,
  DefaultContactSelection2 as DefaultContactSelection,
  DefaultContactStatsSelection2 as DefaultContactStatsSelection,
  DefaultContactTagSelection2 as DefaultContactTagSelection,
  DefaultConversationSelection2 as DefaultConversationSelection,
  DefaultCsvImportSelection2 as DefaultCsvImportSelection,
  DefaultCustomCouponCodeSelection2 as DefaultCustomCouponCodeSelection,
  DefaultCustomCouponCodeUsedSelection2 as DefaultCustomCouponCodeUsedSelection,
  DefaultCustomerSelection2 as DefaultCustomerSelection,
  DefaultCustomerStatsSelection2 as DefaultCustomerStatsSelection,
  DefaultFlowButtonResponseSelection2 as DefaultFlowButtonResponseSelection,
  DefaultFlowButtonSelection2 as DefaultFlowButtonSelection,
  DefaultFlowEdgeSelection2 as DefaultFlowEdgeSelection,
  DefaultFlowNodeSelection2 as DefaultFlowNodeSelection,
  DefaultGaiaWebhookSelection2 as DefaultGaiaWebhookSelection,
  DefaultGlobalVariableSelection2 as DefaultGlobalVariableSelection,
  DefaultLibraryFlowSelection2 as DefaultLibraryFlowSelection,
  DefaultMediaSelection2 as DefaultMediaSelection,
  DefaultMessageSelection2 as DefaultMessageSelection,
  DefaultMessageSkeletonSelection2 as DefaultMessageSkeletonSelection,
  DefaultOfficialWhatsappTemplateSelection2 as DefaultOfficialWhatsappTemplateSelection,
  DefaultOptinFormCompletionSelection2 as DefaultOptinFormCompletionSelection,
  DefaultProductLastUpdateSelection2 as DefaultProductLastUpdateSelection,
  DefaultRedirectionSelection2 as DefaultRedirectionSelection,
  DefaultReplyButtonSelection2 as DefaultReplyButtonSelection,
  DefaultReplyListSelection2 as DefaultReplyListSelection,
  DefaultSamsungFormSelection2 as DefaultSamsungFormSelection,
  DefaultScenarioSelection2 as DefaultScenarioSelection,
  DefaultSegmentMembershipSelection2 as DefaultSegmentMembershipSelection,
  DefaultSegmentSelection2 as DefaultSegmentSelection,
  DefaultSendTemplateQueueSelection2 as DefaultSendTemplateQueueSelection,
  DefaultSendingSelection2 as DefaultSendingSelection,
  DefaultSendingStoppedSelection2 as DefaultSendingStoppedSelection,
  DefaultSendingSummarySelection2 as DefaultSendingSummarySelection,
  DefaultSessionSelection2 as DefaultSessionSelection,
  DefaultShopifyAppInstallationSelection2 as DefaultShopifyAppInstallationSelection,
  DefaultShopifyAppPurchaseOneTimeSelection2 as DefaultShopifyAppPurchaseOneTimeSelection,
  DefaultShopifyAppSubscriptionSelection2 as DefaultShopifyAppSubscriptionSelection,
  DefaultShopifyCartLineItemSelection2 as DefaultShopifyCartLineItemSelection,
  DefaultShopifyCartSelection2 as DefaultShopifyCartSelection,
  DefaultShopifyCheckoutLineItemSelection2 as DefaultShopifyCheckoutLineItemSelection,
  DefaultShopifyCheckoutSelection2 as DefaultShopifyCheckoutSelection,
  DefaultShopifyCustomerSelection2 as DefaultShopifyCustomerSelection,
  DefaultShopifyDraftOrderSelection2 as DefaultShopifyDraftOrderSelection,
  DefaultShopifyFileSelection2 as DefaultShopifyFileSelection,
  DefaultShopifyFulfillmentEventSelection2 as DefaultShopifyFulfillmentEventSelection,
  DefaultShopifyFulfillmentLineItemSelection2 as DefaultShopifyFulfillmentLineItemSelection,
  DefaultShopifyFulfillmentSelection2 as DefaultShopifyFulfillmentSelection,
  DefaultShopifyGdprRequestSelection2 as DefaultShopifyGdprRequestSelection,
  DefaultShopifyInventoryItemSelection2 as DefaultShopifyInventoryItemSelection,
  DefaultShopifyInventoryLevelSelection2 as DefaultShopifyInventoryLevelSelection,
  DefaultShopifyLocationSelection2 as DefaultShopifyLocationSelection,
  DefaultShopifyOrderLineItemSelection2 as DefaultShopifyOrderLineItemSelection,
  DefaultShopifyOrderSelection2 as DefaultShopifyOrderSelection,
  DefaultShopifyPlanSelection2 as DefaultShopifyPlanSelection,
  DefaultShopifyProductImageSelection2 as DefaultShopifyProductImageSelection,
  DefaultShopifyProductMediaSelection2 as DefaultShopifyProductMediaSelection,
  DefaultShopifyProductSelection2 as DefaultShopifyProductSelection,
  DefaultShopifyProductVariantSelection2 as DefaultShopifyProductVariantSelection,
  DefaultShopifyShippingAddressSelection2 as DefaultShopifyShippingAddressSelection,
  DefaultShopifyShopSelection2 as DefaultShopifyShopSelection,
  DefaultShopifySyncSelection2 as DefaultShopifySyncSelection,
  DefaultStripeWebhookSelection2 as DefaultStripeWebhookSelection,
  DefaultTagSelection2 as DefaultTagSelection,
  DefaultUrlClickEventSelection2 as DefaultUrlClickEventSelection,
  DefaultUserCustomerSelection2 as DefaultUserCustomerSelection,
  DefaultUserSelection2 as DefaultUserSelection,
  DefaultWaitingNodeSelection2 as DefaultWaitingNodeSelection,
  DefaultWaitingSelection2 as DefaultWaitingSelection,
  DefaultWebhookNodeSelection2 as DefaultWebhookNodeSelection,
  DefaultWhatsappContactSelection2 as DefaultWhatsappContactSelection,
  DefaultWhatsappPartnerWebhookSelection2 as DefaultWhatsappPartnerWebhookSelection,
  DefaultWhatsappRuleSelection2 as DefaultWhatsappRuleSelection,
  DefaultWhatsappTemplateSelection2 as DefaultWhatsappTemplateSelection,
  DefaultWhatsappTriggerSelection2 as DefaultWhatsappTriggerSelection,
  DefaultWhatsappWebhookSelection2 as DefaultWhatsappWebhookSelection,
  GetsimioClient,
  maybeGetAuthenticationModeOptionsFromClientOptions
};
//# sourceMappingURL=Client.js.map
